/*
 * TDM — made-to-order seats configurator (<tdm-canape-configurator>).
 *
 * Model data comes from snippets/tdm-canape-configurator.liquid; fabrics are fetched on
 * demand from templates/collection.tdm-fabrics.liquid. The product's variants are
 * size (× a second option: depth, armrests, version or finish), priced from the seat price
 * list (structure + eco-participation, without fabric). Horizon's variant picker (hidden,
 * still in the page) keeps the selected variant and the gallery. Price = the variant +
 * the fabric's metres (uni or motif) × the chosen colour's real price; "Ajouter au panier"
 * adds the seat and the fabric (10 cm units) as one group, which the TDM Formulaires cart
 * function merges into one cart line. Products that still have a fabric price category
 * option (Catégorie A–D) keep the earlier behaviour (one variant per category). This element:
 *   1. shows the current model and a sheet of the other made-to-order models,
 *   2. shows the sizes as cards with a top-view plan, the second option, and greys out
 *      the sizes a value does not exist in; selects them in the picker,
 *   3. shows the free choices (feet, backrest fill, side…), sent with the order,
 *   4. opens a fabric browser (search, filters: colour, aspect, material, price) and
 *      prices every fabric for the selected size,
 *   5. sums the seat up (plan drawn in the chosen fabric), lists the options on quote, and
 *      asks for a confirmation before "Ajouter au panier",
 *   6. writes the choice into the cart form as line item properties, and adds the seat and
 *      its fabric to the cart,
 *   7. opens a quote / question form (Shopify contact form) prefilled with the seat,
 *   8. adds a sample of the chosen colour to the cart.
 * Wording follows the selected size's type ("Votre fauteuil", "Vérifiez votre pouf").
 * No imports, so a load failure cannot take the product page down.
 */
(() => {
  if (customElements.get('tdm-canape-configurator')) return;

  const PROPERTIES = {
    fabric: 'properties[Tissu]',
    colour: 'properties[Coloris]',
    reference: 'properties[Réf. tissu]',
    leadTime: 'properties[Fabrication]',
    yardage: 'properties[_Métrage tissu]',
  };

  const SAMPLE_COLLECTION = '/collections/echantillon-tissus/products.json';

  /** Fabric price bands for the "Prix" filter (exact-price products), by price per metre. */
  const PRICE_BANDS = [
    { label: 'Jusqu’à 85 €/m', max: 85 },
    { label: '86 à 95 €/m', max: 95 },
    { label: '96 à 110 €/m', max: 110 },
    { label: 'Plus de 110 €/m', max: Infinity },
  ];

  /** Euros, with cents only when there are some ("2 649,50 €", "3 071 €"). */
  const MONEY = {};
  const moneyFormat = (currency) => {
    if (!MONEY[currency]) {
      MONEY[currency] = [0, 2].map(
        (digits) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits })
      );
    }
    return {
      format: (euros) => {
        const cents = Math.round(Number(euros) * 100);
        return MONEY[currency][cents % 100 ? 1 : 0].format(cents / 100);
      },
    };
  };
  // Shared by every model, so a fabric chosen on Carmel is kept when opening Chicago.
  const FABRIC_STORAGE_KEY = 'tdm-canape-tissu';

  /* Noun of each seat type, for the wording. */
  const TYPE_NOUNS = {
    canape: 'canapé',
    fauteuil: 'fauteuil',
    chauffeuse: 'chauffeuse',
    meridienne: 'méridienne',
    chaise: 'chaise',
    banquette: 'banquette',
    pouf: 'pouf',
    module: 'module',
    accessoire: 'accessoire',
  };

  /* Model sheet tabs (Shopify product types). */
  const MODEL_TABS = [
    ['canapés', 'Canapés'],
    ['fauteuils', 'Fauteuils'],
    ['méridiennes', 'Méridiennes'],
    ['chaises', 'Chaises'],
    ['banquettes', 'Banquettes'],
    ['poufs', 'Poufs'],
  ];

  // Seat use from the team's sheet "Recommandations tissus mobilier" (fabric metafield
  // tdm.recommandation_siege): recommended fabrics first, the others with a warning.
  const SEAT_USES = {
    tous: { label: 'Tous sièges', heading: 'Recommandés pour tous les sièges' },
    sans_coutures: {
      label: 'Sans coutures',
      heading: "Pour sièges sans coutures d'assemblage",
      warning: "Pour sièges sans coutures d'assemblage uniquement",
    },
    decoratif: { label: 'Décoratif', heading: 'Usage décoratif', warning: 'Tissu fragile, usage décoratif' },
  };
  const SEAT_USE_ORDER = Object.keys(SEAT_USES);

  const FAMILY_LABELS = {
    uni: 'Unis',
    unis: 'Unis',
    velours: 'Velours',
    motif: 'Motifs',
    motifs: 'Motifs',
    rayure: 'Rayures',
    rayures: 'Rayures',
    'faux uni': 'Faux unis',
    'faux unis': 'Faux unis',
    carreaux: 'Carreaux',
    jacquard: 'Jacquards',
    jacquards: 'Jacquards',
    jasquard: 'Jacquards',
    toile: 'Toiles',
    toiles: 'Toiles',
    imprime: 'Imprimés',
    imprimes: 'Imprimés',
  };

  const MATERIAL_LABELS = {
    'trevira cs': 'Trevira CS',
  };

  /* Colour families, matched on the French colour name first (swatch colours in the
     admin are often missing or approximate), then on the swatch hex. */
  const COLOURS = [
    { id: 'blanc', label: 'Blancs & écrus', dot: '#f3eee4', words: ['blanc', 'blanche', 'ivoire', 'craie', 'neige', 'nacre', 'ecru', 'perle', 'creme', 'lait', 'coquille'] },
    { id: 'naturel', label: 'Beiges & naturels', dot: '#d9c9ad', words: ['beige', 'lin', 'linum', 'naturel', 'nature', 'sable', 'mastic', 'pierre', 'tuffeau', 'corde', 'ficelle', 'grege', 'chanvre', 'argile', 'tourterelle', 'bouleau', 'nude', 'paille'] },
    { id: 'brun', label: 'Bruns & camel', dot: '#8a6a4f', words: ['taupe', 'chocolat', 'noisette', 'bronze', 'mordore', 'cognac', 'caramel', 'camel', 'noix', 'ecorce', 'rocher', 'alezan', 'cachou', 'gravillon', 'lentille', 'marron', 'castor', 'tabac', 'havane'] },
    { id: 'gris', label: 'Gris & noirs', dot: '#6b6b6b', words: ['gris', 'grise', 'anthracite', 'graphite', 'noir', 'carbone', 'carbonne', 'caviar', 'elephant', 'brume', 'argent', 'galet', 'souris', 'granit', 'orage', 'ardoise', 'plomb'] },
    { id: 'bleu', label: 'Bleus', dot: '#34507a', words: ['bleu', 'marine', 'indigo', 'canard', 'petrole', 'turquoise', 'encre', 'aqua', 'lagon', 'cobalt', 'phtalo', 'jean', 'jeans', 'saxe', 'paon', 'nuit', 'ciel'] },
    { id: 'vert', label: 'Verts', dot: '#5f7a52', words: ['vert', 'verte', 'kaki', 'celadon', 'sauge', 'malachite', 'chlorophylle', 'emeraude', 'amande', 'nordmann', 'cedre', 'lichen', 'mousse', 'olive', 'sylvestre', 'tilleul', 'anis'] },
    { id: 'jaune', label: 'Jaunes & orangés', dot: '#d29a3c', words: ['jaune', 'moutarde', 'curry', 'mimosa', 'tournesol', 'tournsole', 'or', 'safran', 'orange', 'citrouille', 'tomette', 'ocre', 'citron', 'miel', 'gingembre', 'laiton', 'cuivre', 'abricot', 'ambre'] },
    { id: 'rouge', label: 'Rouges & roses', dot: '#a73a3e', words: ['rouge', 'cerise', 'rubis', 'framboise', 'rose', 'corail', 'piment', 'vermeil', 'garance', 'tomate', 'pivoine', 'vermillon', 'brique', 'feu', 'grenat', 'petale'] },
    { id: 'violet', label: 'Violets & prunes', dot: '#5e3a58', words: ['prune', 'violet', 'violette', 'figue', 'aubergine', 'amethyste', 'cyclamen', 'bruyere', 'lilas', 'mauve'] },
  ];

  const PHRASES = [
    ['lie de vin', 'violet'],
    ['bois de rose', 'rouge'],
  ];

  const SORTS = [
    ['nom', 'Nom du tissu'],
    ['prix', 'Prix croissant'],
    ['prix-desc', 'Prix décroissant'],
  ];

  const ICONS = {
    search:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.5 4.5"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    chevron: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 6 6 6-6 6"/></svg>',
    chevronDown: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m7 10 5 5 5-5"/></svg>',
    check: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    scissors:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12"/></svg>',
    clock:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>',
    message:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.5 5.5h15v10h-9l-4 3.5v-3.5h-2z"/><path d="M8 9.5h8M8 12.5h5"/></svg>',
    info: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 11v5M12 8h.01"/></svg>',
    plus: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 6v12M6 12h12"/></svg>',
  };

  /* ---------- Helpers ---------- */

  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const normalize = (value) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();

  const capitalize = (value) => {
    const text = String(value ?? '').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  };

  const formatNumber = (value, digits = 0) =>
    Number(value).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

  const asList = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch (error) {
        return [value];
      }
    }
    return [];
  };

  const readStorage = (key) => {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage unavailable: the choice simply is not remembered */
    }
  };

  const hexFamily = (hex) => {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) return null;
    const int = parseInt(match[1], 16);
    const r = ((int >> 16) & 255) / 255;
    const g = ((int >> 8) & 255) / 255;
    const b = (int & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (l > 0.88) return 'blanc';
    if (s < 0.14) return l < 0.6 ? 'gris' : 'naturel';
    let h = 0;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
    if (h < 15 || h >= 340) return 'rouge';
    if (h < 45) return l < 0.45 ? 'brun' : 'jaune';
    if (h < 70) return 'jaune';
    if (h < 170) return 'vert';
    if (h < 260) return 'bleu';
    if (h < 300) return 'violet';
    return 'rouge';
  };

  const colourFamily = (label, hex) => {
    const text = ` ${normalize(label).replace(/[^a-z]+/g, ' ')} `;
    for (const [phrase, family] of PHRASES) {
      if (text.includes(` ${phrase} `)) return family;
    }
    for (const word of text.trim().split(/\s+/)) {
      const family = COLOURS.find((entry) => entry.words.includes(word));
      if (family) return family.id;
    }
    return hexFamily(hex);
  };

  /** "adrien-naturel" → "Naturel" (values written as handle-colour, without the " - " the theme strips). */
  const colourLabel = (label, handle) => {
    const text = String(label ?? '').trim();
    const prefix = `${handle}-`;
    if (handle && normalize(text).startsWith(prefix)) return capitalize(text.slice(prefix.length).replace(/-/g, ' '));
    return text;
  };

  let planCount = 0;

  /**
   * Top view of a size, drawn to a shared scale so the cards compare (units: cm).
   * Plans: 1 / 2 / 3 / canape (two arms), chauffeuse (none), manchette (one arm, `side`),
   * convertible, table (sofa + coffee table), angle-gauche / angle-droite, angle-module,
   * meridienne / liseuse (drawn lengthwise), pouf, pouf-rond, banquette, chaise, cabriolet.
   * With `fabric` (an image URL) the upholstery is drawn in that fabric.
   */
  const planSvg = (spec, fabric = '', side = 'left') => {
    const W = 266;
    const H = 168;
    const plan = String(spec.plan || '2');
    let width = Number(spec.largeur) || 180;
    let depth = Number(spec.profondeur) || 88;
    const arm = Number(spec.largeur_accoudoir) || 10;
    const back = 18;
    const patternId = fabric ? `tdm-plan-fabric-${(planCount += 1)}` : '';
    const fill = patternId ? ` style="fill: url(#${patternId})"` : '';
    const rect = (x, y, w, h, cls, radius = 2) =>
      `<rect class="${cls}" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="${radius}"${fill}/>`;
    const shade = (x, y, w, h) =>
      patternId
        ? `<rect class="tdm-plan__shade" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="2"/>`
        : '';
    const frame = (x, y, w, h) => rect(x, y, w, h, 'tdm-plan__frame') + shade(x, y, w, h);
    const line = (x1, y1, x2, y2) =>
      `<line class="tdm-plan__seam" x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"/>`;
    let shapes = '';

    // Lengthwise pieces: the long side runs across the card.
    if (plan === 'meridienne') [width, depth] = [Math.max(width, depth), Math.min(width, depth)];

    const x0 = (W - width) / 2;
    const y0 = (H - depth) / 2;

    if (plan === 'pouf-rond') {
      shapes += `<circle class="tdm-plan__body" cx="${W / 2}" cy="${H / 2}" r="${(width / 2).toFixed(1)}"${fill}/>`;
    } else if (plan === 'pouf' || plan === 'banquette' || plan === 'tetiere') {
      shapes += rect(x0, y0, width, depth, 'tdm-plan__body', plan === 'pouf' ? 4 : 2);
    } else if (plan === 'chaise' || plan === 'cabriolet') {
      shapes += rect(x0, y0, width, depth, 'tdm-plan__body', plan === 'cabriolet' ? 10 : 4);
      shapes += frame(x0, y0, width, 12);
      if (plan === 'cabriolet') {
        shapes += frame(x0, y0, arm, depth * 0.8);
        shapes += frame(x0 + width - arm, y0, arm, depth * 0.8);
      }
    } else if (plan === 'liseuse' || plan === 'meridienne') {
      // Seat lengthwise, backrest along part of the back, arm at the head end.
      const head = side === 'right';
      shapes += rect(x0, y0, width, depth, 'tdm-plan__body');
      shapes += frame(head ? x0 + width - arm : x0, y0, arm, depth);
      shapes += frame(head ? x0 + width * 0.6 : x0, y0, width * 0.4, 16);
    } else if (plan === 'angle-module') {
      shapes += rect(x0, y0, width, depth, 'tdm-plan__body');
      shapes += frame(x0, y0, width, back);
      shapes += frame(side === 'right' ? x0 + width - back : x0, y0, back, depth);
    } else if (plan === 'angle-gauche' || plan === 'angle-droite') {
      const total = width;
      const chaiseDepth = depth;
      const chaiseWidth = Number(spec.largeur_chaise) || 89;
      const sofaDepth = Number(spec.profondeur_canape) || 88;
      const sofaWidth = total - chaiseWidth;
      const left = plan === 'angle-gauche';
      const chaiseX = left ? x0 : x0 + sofaWidth;
      const sofaX = left ? x0 + chaiseWidth : x0;
      shapes += rect(chaiseX, y0, chaiseWidth, chaiseDepth, 'tdm-plan__body');
      shapes += rect(sofaX, y0, sofaWidth, sofaDepth, 'tdm-plan__body');
      shapes += frame(x0, y0, total, back);
      shapes += frame(left ? x0 : x0 + total - arm, y0, arm, chaiseDepth);
      shapes += frame(left ? x0 + total - arm : x0, y0, arm, sofaDepth);
      const innerStart = left ? sofaX : sofaX + arm;
      const innerEnd = left ? x0 + total - arm : chaiseX;
      shapes += line(left ? sofaX : chaiseX, y0 + back, left ? sofaX : chaiseX, y0 + sofaDepth);
      shapes += line((innerStart + innerEnd) / 2, y0 + back, (innerStart + innerEnd) / 2, y0 + sofaDepth);
    } else {
      // Sofas and armchairs: 1 / 2 / 3 / canape / chauffeuse / manchette / convertible / table.
      const arms = plan === 'chauffeuse' ? 'none' : plan === 'manchette' ? side : 'both';
      const table = plan === 'table' ? Number(spec.largeur_table) || 70 : 0;
      const seatWidth = width - table;
      const seats = Number(spec.assises) || (plan === '1' ? 1 : 2);
      const sx = side === 'right' && table ? x0 + table : x0;
      shapes += rect(sx, y0, seatWidth, depth, 'tdm-plan__body');
      shapes += frame(sx, y0, seatWidth, back);
      const leftArm = arms === 'both' || arms === 'left';
      const rightArm = (arms === 'both' && !table) || arms === 'right';
      if (leftArm) shapes += frame(sx, y0, arm, depth);
      if (rightArm) shapes += frame(sx + seatWidth - arm, y0, arm, depth);
      if (table) shapes += frame(side === 'right' && table ? x0 : x0 + seatWidth, y0 + depth * 0.18, table, depth * 0.82);
      const innerStart = sx + (leftArm ? arm : 0);
      const innerEnd = sx + seatWidth - (rightArm ? arm : 0);
      for (let index = 1; index < seats; index += 1) {
        const x = innerStart + ((innerEnd - innerStart) * index) / seats;
        shapes += line(x, y0 + back, x, y0 + depth);
      }
      if (plan === 'convertible') {
        // The bed, folded inside: a dashed outline under the seat.
        shapes += `<rect class="tdm-plan__bed" x="${(innerStart + 6).toFixed(1)}" y="${(y0 + back + 8).toFixed(1)}" width="${(innerEnd - innerStart - 12).toFixed(1)}" height="${(depth - back - 16).toFixed(1)}" rx="2"/>`;
      }
    }
    const defs = patternId
      ? `<defs><pattern id="${patternId}" patternUnits="userSpaceOnUse" width="48" height="48"><image href="${escapeHtml(
          fabric
        )}" width="48" height="48" preserveAspectRatio="xMidYMid slice"/></pattern></defs>`
      : '';
    return `<svg class="tdm-plan${patternId ? ' tdm-plan--fabric' : ''}" viewBox="0 0 ${W} ${H}" aria-hidden="true" focusable="false">${defs}${shapes}</svg>`;
  };

  /* ---------- Option illustrations (same line style as the plans) ---------- */

  const n1 = (value) => Number(value).toFixed(1);
  const art = (body, kind = '') =>
    `<svg class="tdm-art" viewBox="0 0 120 72" aria-hidden="true" focusable="false"${kind ? ` data-art="${kind}"` : ''}>${body}</svg>`;
  const box = (x, y, w, h, cls, radius = 2, style = '') =>
    `<rect class="${cls}" x="${n1(x)}" y="${n1(y)}" width="${n1(w)}" height="${n1(h)}" rx="${radius}"${style ? ` style="${style}"` : ''}/>`;
  const stroke = (x1, y1, x2, y2, cls = 'tdm-plan__seam') =>
    `<line class="${cls}" x1="${n1(x1)}" y1="${n1(y1)}" x2="${n1(x2)}" y2="${n1(y2)}"/>`;
  const poly = (points, cls, style = '') =>
    `<polygon class="${cls}" points="${points.map(([x, y]) => `${n1(x)},${n1(y)}`).join(' ')}"${style ? ` style="${style}"` : ''}/>`;
  const ground = (y = 66) => stroke(4, y, 116, y, 'tdm-art__ground');
  /** "assise 46 cm" → 46 */
  const cmIn = (text) => {
    const match = /(\d+(?:[.,]\d+)?)\s*cm/.exec(String(text || ''));
    return match ? Number(match[1].replace(',', '.')) : null;
  };

  /** Wood and metal tones for feet and plinths, from the value's wording. */
  const MATERIALS = [
    [/chrom/, '#cfd4d8'],
    [/noir/, '#2b2b2b'],
    [/fonce|praline|wenge|noyer/, '#6f4b33'],
    [/clair|naturel|hetre|chene|bois/, '#d8ba8e'],
  ];
  const materialColour = (value) =>
    (MATERIALS.find(([pattern]) => pattern.test(normalize(value))) || [null, '#c8b49a'])[1];

  /**
   * Side view: seat depth (the arrow) and, for convertibles, the bed unfolded (dashed).
   * The values of one option share `span` so their cards compare.
   */
  const profileArt = ({ depth, seat, span, bed = 0, arrow = true }) => {
    const s = Math.min(104 / Math.max(span, bed || 0), 0.62);
    const x0 = (120 - Math.max(span, bed || 0) * s) / 2;
    const X = (cm) => x0 + cm * s;
    const Y = (cm) => 66 - cm * s;
    const back = Math.max(depth - seat, 14);
    let body = ground();
    body += box(X(3), Y(10), 4, 10 * s, 'tdm-art__leg', 1);
    body += box(X(depth - 3) - 4, Y(10), 4, 10 * s, 'tdm-art__leg', 1);
    if (bed) {
      body += box(X(back), Y(36), (bed - back) * s, 11 * s, 'tdm-plan__bed', 2);
      body += stroke(X(bed - 6), Y(25), X(bed - 6), Y(0));
    }
    body += box(X(0), Y(28), depth * s, 18 * s, 'tdm-plan__frame');
    body += box(X(0), Y(80), back * 0.45 * s, 52 * s, 'tdm-plan__frame');
    body += box(X(back * 0.45), Y(72), back * 0.55 * s, 32 * s, 'tdm-plan__body', 4);
    body += box(X(back), Y(42), seat * s, 14 * s, 'tdm-plan__body', 3);
    if (arrow) {
      const y = Y(52);
      body += stroke(X(back) + 1, y, X(depth) - 1, y, 'tdm-art__dim');
      body += stroke(X(back) + 1, y - 3, X(back) + 1, y + 3, 'tdm-art__dim');
      body += stroke(X(depth) - 1, y - 3, X(depth) - 1, y + 3, 'tdm-art__dim');
    }
    return art(body, 'profile');
  };

  /** Front view of one end: the armrest's width and shape (fins, ronds, droits). */
  const armArt = (width, round) => {
    const w = Math.max(width, 3) * 1.7;
    const x = 16;
    const top = 20;
    let body = ground();
    body += box(x + 3, 58, 4, 8, 'tdm-art__leg', 1) + box(104, 58, 4, 8, 'tdm-art__leg', 1);
    body += box(x + w, 8, 112 - x - w, 26, 'tdm-plan__frame');
    body += box(x + w + 1, 13, 110 - x - w, 21, 'tdm-plan__body', 3);
    body += box(x, 42, 112 - x, 16, 'tdm-plan__frame');
    body += box(x + w + 1, 33, 110 - x - w, 10, 'tdm-plan__body', 3);
    body += round
      ? `<path class="tdm-plan__frame" d="M${n1(x)} 58 V${n1(top + w / 2)} A${n1(w / 2)} ${n1(w / 2)} 0 0 1 ${n1(x + w)} ${n1(top + w / 2)} V58 Z"/>`
      : box(x, top, w, 58 - top, 'tdm-plan__frame', 1);
    return art(body, 'arm');
  };

  /** "Couture plateau et angles" → "plateau", "Avec passepoil" → "passepoil", "Couture aux angles" → "angles". */
  const seamKind = (value) => {
    const text = normalize(value);
    if (/passepoil/.test(text) && !/sans passepoil/.test(text)) return 'passepoil';
    if (/plateau/.test(text)) return 'plateau';
    if (/angle/.test(text)) return 'angles';
    return '';
  };

  /**
   * A block (pouf, or bench when `long`) in three-quarter view: its seams in the accent colour —
   * piping along the edges, stitching around the top and down the corners, or down the corners
   * only — and, for the cover, staples (fixed) or a zip (removable).
   */
  const blockArt = ({ seams = '', cover = '', long = false }) => {
    const [x0, x1, top, bottom] = long ? [10, 86, 34, 54] : [22, 74, 30, 64];
    const dx = 22;
    const dy = -14;
    const topFace = [[x0, top], [x0 + dx, top + dy], [x1 + dx, top + dy], [x1, top]];
    const sideFace = [[x1, top], [x1 + dx, top + dy], [x1 + dx, bottom + dy], [x1, bottom]];
    let body = ground(long ? 62 : 68);
    body += poly(sideFace, 'tdm-art__side') + box(x0, top, x1 - x0, bottom - top, 'tdm-plan__body', 0) + poly(topFace, 'tdm-art__top');
    const ring = [...topFace, topFace[0]];
    const topEdges = topFace.map((point, index) => [...point, ...ring[index + 1]]);
    const corners = [
      [x0, top, x0, bottom],
      [x1, top, x1, bottom],
      [x1 + dx, top + dy, x1 + dx, bottom + dy],
    ];
    if (seams === 'passepoil') {
      body += [...topEdges, [x0, bottom, x1, bottom], [x1, bottom, x1 + dx, bottom + dy]]
        .map((edge) => stroke(...edge, 'tdm-art__piping'))
        .join('');
    } else if (seams === 'plateau') {
      body += [...topEdges, ...corners].map((edge) => stroke(...edge, 'tdm-art__stitch')).join('');
    } else if (seams === 'angles') {
      body += corners.map((edge) => stroke(...edge, 'tdm-art__stitch')).join('');
    }
    if (cover === 'fixe') {
      // Staples under the front edge.
      for (let x = x0 + 8; x < x1 - 4; x += 12) {
        body += `<path class="tdm-art__staple" d="M${x} ${bottom + 5} v-3 h5 v3"/>`;
      }
    } else if (cover === 'dehoussable') {
      const y = bottom - 4;
      body += stroke(x0 + 5, y, x1 - 9, y, 'tdm-art__zip') + stroke(x1, y, x1 + dx - 4, y + dy + 3, 'tdm-art__zip');
      body += box(x1 - 10, y - 2.5, 5, 7, 'tdm-art__tab', 1);
    }
    return art(body, 'block');
  };

  const finishArt = (value, long) => blockArt({ seams: seamKind(value), cover: /dehoussable/.test(normalize(value)) ? 'dehoussable' : '', long });

  /** A corner of the sofa's base with its feet (shape and material) or its plinth. */
  const feetArt = (value, plinth) => {
    const text = normalize(value);
    const style = `fill: ${materialColour(value)}`;
    let body = box(10, 14, 100, 14, 'tdm-plan__frame') + box(10, 28, 100, 10, 'tdm-plan__body', 1);
    if (plinth) return art(body + ground(52) + box(18, 38, 84, 14, 'tdm-art__material', 1, style), 'feet');
    body += ground(64);
    const metal = /metal|chrom/.test(text) || (/noir/.test(text) && !/bois/.test(text));
    [24, 96].forEach((cx) => {
      if (metal) {
        body += box(cx - 2, 38, 4, 26, 'tdm-art__material', 1, style);
        if (/chrom/.test(text)) body += stroke(cx - 0.6, 40, cx - 0.6, 62, 'tdm-art__shine');
      } else if (/rond/.test(text)) {
        body += box(cx - 3.5, 38, 7, 26, 'tdm-art__material', 3.5, style);
      } else if (/trapeze/.test(text)) {
        body += poly([[cx - 8, 38], [cx + 8, 38], [cx + 4, 64], [cx - 4, 64]], 'tdm-art__material', style);
      } else {
        body += poly([[cx - 5, 38], [cx + 5, 38], [cx + 2.5, 64], [cx - 2.5, 64]], 'tdm-art__material', style);
      }
    });
    return art(body, 'feet');
  };

  /** Back cushion: a feather for down, wavy lines for fibre. */
  const backArt = (value) => {
    let body = box(22, 10, 76, 52, 'tdm-plan__body', 12);
    if (/duvet|plume/.test(normalize(value))) {
      body += '<path class="tdm-art__top" d="M46 52 C 48 34 64 22 82 18 C 78 32 64 46 46 52 Z"/>';
      body += '<path class="tdm-art__line" d="M42 56 C 56 44 68 32 82 18"/>';
    } else {
      [26, 34, 42, 50].forEach((y) => {
        body += `<path class="tdm-art__line" d="M32 ${y} q 7 -5 14 0 t 14 0 t 14 0 t 14 0"/>`;
      });
    }
    return art(body, 'back');
  };

  /** Seat cushion in section: foam, with a down layer on top for the "complexée" seat. */
  const seatArt = (value) => {
    const layered = /complex|duvet|plume/.test(normalize(value));
    const foamTop = layered ? 34 : 24;
    let body = box(16, foamTop, 88, 58 - foamTop, 'tdm-art__top', 3);
    for (let y = foamTop + 6; y < 56; y += 8) {
      for (let x = 24; x < 100; x += 10) body += `<circle class="tdm-art__pore" cx="${x + ((y / 2) % 2) * 5}" cy="${y}" r="1.3"/>`;
    }
    body += layered
      ? '<path class="tdm-plan__body" d="M16 34 V24 q 11 -7 22 0 t 22 0 t 22 0 t 22 0 V34 Z"/>'
      : box(16, 18, 88, 6, 'tdm-plan__body', 2);
    return art(body, 'seat');
  };

  /** Front view: fitted cover on visible feet, or a loose cover down to the floor. */
  const coverArt = (value) => {
    const loose = /flottant|cache/.test(normalize(value));
    let body = ground(64);
    body += box(12, 10, 96, 20, 'tdm-plan__frame');
    body += box(14, 14, 44, 16, 'tdm-plan__body', 3) + box(62, 14, 44, 16, 'tdm-plan__body', 3);
    body += box(12, 30, 96, 12, 'tdm-plan__body', 2);
    if (loose) {
      body += '<path class="tdm-plan__body" d="M12 42 H108 L110 63 H10 Z"/>';
      [30, 48, 72, 90].forEach((x) => (body += stroke(x, 48, x + (x < 60 ? -1 : 1), 62)));
    } else {
      body += box(12, 42, 96, 12, 'tdm-plan__body', 1);
      body += box(18, 54, 5, 10, 'tdm-art__leg', 1) + box(97, 54, 5, 10, 'tdm-art__leg', 1);
    }
    return art(body, 'cover');
  };

  const image = (url, className, width = 160) =>
    url
      ? `<img class="${className}" src="${escapeHtml(url)}" alt="" width="${width}" height="${width}" loading="lazy">`
      : `<span class="${className} ${className}--empty"></span>`;

  /** Product type (plural) → the word on the link to that version: "fauteuils" → "Fauteuil". */
  const TYPE_SINGULAR = {
    'canapés': 'Canapé',
    fauteuils: 'Fauteuil',
    'méridiennes': 'Méridienne',
    chaises: 'Chaise',
    banquettes: 'Banquette',
    poufs: 'Pouf',
  };

  const modelName = (title) => title.replace(/\s*[—–-]\s*sur mesure.*$/i, '').replace(/^(canapé|fauteuil|pouf|chaise|banquette|méridienne)\s+/i, '');

  /* ---------- Element ---------- */

  class TdmCanapeConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.data) return;

      try {
        this.data = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '');
      } catch (error) {
        return;
      }
      if (!this.data?.variants?.length) return;

      // The quote form is rendered by Liquid (Shopify contact form); keep it before re-rendering.
      this.quoteTemplate = this.querySelector('template[data-quote-template]');

      const config = this.data.config || {};
      this.config = config;
      this.specs = config.tailles || {};
      // Second option (depth, armrests, version, finish). `profondeurs` = earlier name.
      this.variantOption = config.option_variante || config.option_profondeur || '';
      this.variantLabels = config.variantes || config.profondeurs || {};
      // Two questions for one option (Ocean: cover × seams); the value is the answers joined by ", ".
      this.axes = Array.isArray(config.variante_axes) && config.variante_axes.length > 1 ? config.variante_axes : null;
      this.leadTime = config.delai || '';
      this.choices = config.choix || [];
      this.quoteOptions = config.options_devis || [];
      this.notes = config.notes || [];
      const { options } = this.data;
      // Exact price: no fabric category option. With a "Prix du tissu" option (fabric price per
      // metre), each variant is the whole seat at that fabric price: one cart line, the right
      // price, through the product form. Without it, the structure variant + the fabric line.
      this.exact = options.category == null;
      this.tiered = this.exact && options.tier != null;
      // All the option's values (Liquid lists at most 250 variants: Gaspard has 812).
      this.tierValues = new Set(
        this.tiered ? this.data.tierValues || this.data.variants.map((v) => v.options[options.tier]) : []
      );
      this.categories = this.exact
        ? PRICE_BANDS.map((band) => band.label)
        : Array.from(new Set(this.data.variants.map((v) => v.options[options.category])));
      this.variantValues = options.variant == null ? [] : this.data.variantValues || [];
      this.models = this.data.models || [];
      this.money = moneyFormat(this.data.currency || 'EUR');
      this.section = this.closest('.shopify-section') || document;
      // Paid finishes folded into the second option ("90 cm + housse rubacro": a product has at
      // most 3 options). The cards show the base values; the picker gets the folded value.
      this.supplements = this.tiered && Array.isArray(config.supplements) ? config.supplements : [];
      this.allVariantValues = this.variantValues;
      this.supplementOn = new Set();
      if (this.supplements.length) {
        this.variantValues = this.allVariantValues.filter((value) => !String(value).includes(' + '));
        const current = this.pickerValue(this.allVariantValues) || '';
        this.supplements.forEach((sup) => {
          if (current.endsWith(` + ${sup.suffixe}`)) this.supplementOn.add(sup.nom);
        });
      }
      // Accessories on their own cart line (protection cover, second cover), when on sale.
      this.extras = this.tiered ? (config.extras || []).filter((extra) => this.data.extras?.[extra.handle]) : [];
      this.extraOn = new Set();

      this.fabrics = null;
      this.fabric = null;
      this.colour = null;
      this.pending = null;
      this.filters = { use: new Set(), fam: new Set(), mat: new Set(), col: new Set(), cat: new Set() };
      this.query = '';
      this.sort = 'nom';
      this.confirmed = false;
      this.picked = {};
      this.modelTab = '';

      this.render();
      this.update();

      this.onVariantUpdate = (event) => {
        if (String(event.detail?.data?.productId) !== String(this.data.productId)) return;
        // Horizon has already morphed the picker when it dispatches the event.
        queueMicrotask(() => this.update());
        // The section's own script writes the variant (structure) price after Horizon: show the total again.
        setTimeout(() => this.updatePagePrice(), 0);
      };
      this.onAddToCart = (event) => this.guard(event);
      document.addEventListener('variant:update', this.onVariantUpdate);
      // Capture phase on window: runs before Horizon's delegated click/submit handlers.
      window.addEventListener('click', this.onAddToCart, true);
      window.addEventListener('submit', this.onAddToCart, true);

      const prefetch = () => this.loadFabrics().catch(() => {});
      if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 3000 });
      else setTimeout(prefetch, 1500);

      if (this.data.quote?.sent) this.showBanner();
      else if (this.data.quote?.errors) this.openQuote();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.onVariantUpdate);
      window.removeEventListener('click', this.onAddToCart, true);
      window.removeEventListener('submit', this.onAddToCart, true);
    }

    /* ---------- Horizon variant picker and cart form ---------- */

    get picker() {
      return this.section.querySelector('variant-picker');
    }

    get form() {
      return (
        this.section.querySelector('form[data-type="add-to-cart-form"]') ||
        this.section.querySelector('form[action*="/cart/add"]')
      );
    }

    pickerValue(values) {
      const picker = this.picker;
      if (!picker || !values.length) return null;
      for (const input of picker.querySelectorAll('input[type="radio"]:checked')) {
        if (values.includes(input.value)) return input.value;
      }
      for (const select of picker.querySelectorAll('select')) {
        if (values.includes(select.value)) return select.value;
      }
      return null;
    }

    /**
     * Select option values in the hidden picker in one go: every radio is ticked, then a
     * single change event fires (Horizon requests the variant from all ticked values).
     */
    setPicker(values) {
      const picker = this.picker;
      if (!picker) return;
      let changed = null;
      values
        .filter(Boolean)
        .map((value) => (this.supplements.length && this.variantValues.includes(value) ? this.packVariante(value) : value))
        .forEach((value) => {
        const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = radio;
          return;
        }
        const select = Array.from(picker.querySelectorAll('select')).find((el) =>
          Array.from(el.options).some((option) => option.value === value)
        );
        if (select && select.value !== value) {
          select.value = value;
          changed = select;
        }
      });
      changed?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    get size() {
      return this.pickerValue(this.data.sizes) || this.data.sizes[0];
    }

    get variante() {
      if (!this.variantValues.length) return null;
      const value = this.pickerValue(this.allVariantValues);
      return value ? String(value).split(' + ')[0] : this.variantValues[0];
    }

    /** Second-option value with the chosen paid finishes ("90 cm" → "90 cm + housse rubacro"). */
    packVariante(variante) {
      if (!variante || !this.supplements.length) return variante;
      const parts = this.supplements.filter((sup) => this.supplementOn.has(sup.nom)).map((sup) => sup.suffixe);
      return [variante, ...parts].join(' + ');
    }

    /** Paid finishes chosen, in cents. */
    get supplementCents() {
      return this.supplements.filter((sup) => this.supplementOn.has(sup.nom)).reduce((sum, sup) => sum + Math.round(sup.prix * 100), 0);
    }

    get category() {
      return this.exact ? null : this.pickerValue(this.categories);
    }

    /** The picker value that follows the fabric (its price category); none for exact-price products. */
    get fabricPickerValue() {
      if (this.tiered) return this.fabric && this.colour ? this.tierLabel(this.fabric, this.colour) : null;
      return this.exact ? null : this.fabric?.category;
    }

    findVariant(size, category, variante = this.variante, tier = null) {
      const { size: sizeIndex, category: categoryIndex, variant: variantIndex, tier: tierIndex } = this.data.options;
      return this.data.variants.find(
        (v) =>
          v.options[sizeIndex] === size &&
          (category == null || v.options[categoryIndex] === category) &&
          (variantIndex == null || v.options[variantIndex] === this.packVariante(variante)) &&
          (tier == null || v.options[tierIndex] === tier)
      );
    }

    /**
     * "Prix du tissu" value of a colour: its price per metre, "· motif" for patterned fabrics
     * (they use the pattern-repeat metres). Same text as catalogue-sieges/build.py.
     */
    tierLabel(fabric, colour) {
      const cents = colour.unitPrice * 10;
      const euros = cents % 100 ? (cents / 100).toFixed(2).replace('.', ',') : String(cents / 100);
      return `${euros} €/m${this.isPatterned(fabric) ? ' · motif' : ''}`;
    }

    cheapestColour(fabric) {
      return fabric.colours.reduce((best, colour) => (!best || colour.unitPrice < best.unitPrice ? colour : best), null);
    }

    exists(size, variante = this.variante) {
      return Boolean(this.findVariant(size, null, variante)) || (this.tiered && this.inCatalogue(size, variante));
    }

    /**
     * Fabric-price products with more than 250 variants: the page only lists the first 250, so a
     * size (× second option) is known from the configurator data and priced from its `prix`
     * (structure + eco-participation, the same figure the variants are built from).
     */
    inCatalogue(size, variante = this.variante) {
      const base = this.specs[size];
      if (!base) return false;
      if (!this.variantValues.length || !variante) return true;
      const own = base.variantes || base.profondeurs;
      return own ? Object.prototype.hasOwnProperty.call(own, variante) : this.variantValues.includes(variante);
    }

    basePrice(size, variante = this.variante) {
      const prix = this.structurePrice(size, variante);
      return prix == null ? null : prix + this.supplementCents;
    }

    /** Structure + eco of a size × second option, in cents (without paid finishes). */
    structurePrice(size, variante = this.variante) {
      const spec = this.specFor(size, variante);
      const prix = Number((variante && spec.prix_variantes?.[variante]) ?? spec.prix);
      return prix && this.inCatalogue(size, variante) ? Math.round(prix * 100) : null;
    }

    /* ---------- Accessories on their own cart line ---------- */

    extraAvailable(extra) {
      if (extra.variantes && !extra.variantes.includes(this.variante)) return false;
      if (extra.id === 'housse2') return this.specFor(this.size).prix_housse != null;
      return true;
    }

    /** Price in cents; a second cover needs the fabric (same metres as the seat). */
    extraPrice(extra) {
      if (extra.id !== 'housse2') return Math.round(Number(extra.prix) * 100);
      const spec = this.specFor(this.size);
      if (spec.prix_housse == null || !this.fabric || !this.colour) return null;
      return Math.round(spec.prix_housse * 100) + this.unitsFor(this.size, this.fabric) * this.colour.unitPrice;
    }

    get activeExtras() {
      return this.extras.filter((extra) => this.extraOn.has(extra.id) && this.extraAvailable(extra));
    }

    get extrasTotal() {
      return this.activeExtras.reduce((sum, extra) => sum + (this.extraPrice(extra) || 0), 0);
    }

    /** Seat + accessories (what goes to the cart). */
    get total() {
      const price = this.price;
      return price == null ? null : price + this.extrasTotal;
    }

    /**
     * Variant of an accessory for this configuration: Liquid gives the option value ids, the
     * product page answers with the variant those values select (any of its 2 048 variants).
     */
    async extraVariant(extra) {
      const info = this.data.extras?.[extra.handle];
      if (!info) return null;
      const names = Object.keys(info.options || {});
      if (!names.length || (names.length === 1 && names[0] === 'Title')) return info.variant;
      const wanted = {
        [this.config.option_dimension || 'Dimension']: this.size,
        [this.variantOption]: this.variante,
        [this.config.option_prix_tissu || 'Prix du tissu']: this.fabricPickerValue,
      };
      const ids = names.map((name) => info.options[name]?.[wanted[name]]);
      if (ids.some((id) => !id)) return null;
      const response = await fetch(`${info.url}?view=tdm-variant&option_values=${ids.join(',')}`);
      if (!response.ok) return null;
      const variant = await response.json();
      const options = Array.isArray(variant.options) ? variant.options : [];
      return names.every((name, index) => options[index] === wanted[name]) ? variant : null;
    }

    /** Patterned fabrics need the "motif" metres (pattern repeat). */
    isPatterned(fabric) {
      return Boolean(fabric?.families?.includes('Motifs'));
    }

    /** Fabric metres for a size: the "motif" figure for patterned fabrics when the sheet gives one. */
    metresFor(size, fabric, variante = this.variante) {
      const spec = this.specFor(size, variante);
      const metres = (this.isPatterned(fabric) && Number(spec.metrage_motif)) || Number(spec.metrage_uni) || 0;
      return metres;
    }

    /** Fabric units in the cart (the fabrics are sold by 10 cm). */
    unitsFor(size, fabric, variante = this.variante) {
      return Math.round(this.metresFor(size, fabric, variante) * 10);
    }

    /** Price of 10 cm of a colour (cents); without a colour, the fabric's cheapest one. */
    unitPrice(fabric, colour = null) {
      if (colour) return colour.unitPrice;
      return Math.min(...fabric.colours.map((entry) => entry.unitPrice));
    }

    /**
     * Price (cents) of a size in a fabric. Exact-price products: the variant (structure +
     * eco-participation) + the fabric's units × the colour's price. Category products: the
     * variant of the fabric's category (`fabric` may also be a category name).
     */
    priceFor(size, fabric, variante = this.variante, colour = null) {
      if (!this.exact) {
        const category = typeof fabric === 'string' ? fabric : fabric?.category;
        const variant = this.findVariant(size, category, variante);
        return variant ? variant.price : null;
      }
      if (!fabric || typeof fabric === 'string') return null;
      if (this.tiered) {
        const shade = colour || this.cheapestColour(fabric);
        if (!shade || !this.tierValues.has(this.tierLabel(fabric, shade))) return null;
        const variant = this.findVariant(size, null, variante, this.tierLabel(fabric, shade));
        if (variant) return variant.price;
        const base = this.basePrice(size, variante);
        return base == null ? null : base + this.unitsFor(size, fabric, variante) * shade.unitPrice;
      }
      const variant = this.findVariant(size, null, variante);
      if (!variant) return null;
      return variant.price + this.unitsFor(size, fabric, variante) * this.unitPrice(fabric, colour);
    }

    fromPrice(size, variante = this.variante) {
      if (this.exact) {
        if (!this.fabrics?.length) return null;
        return Math.min(...this.fabrics.map((fabric) => this.priceFor(size, fabric, variante)).filter((p) => p != null));
      }
      const prices = this.categories.map((category) => this.priceFor(size, category, variante)).filter((p) => p != null);
      return prices.length ? Math.min(...prices) : null;
    }

    /** Price breakdown of the selected seat (exact-price products), in cents. */
    get breakdown() {
      if (!this.exact || !this.complete) return null;
      const size = this.size;
      const variant = this.findVariant(size, null, this.variante);
      if (!variant && !(this.tiered && this.exists(size))) return null;
      const spec = this.specFor(size);
      const units = this.unitsFor(size, this.fabric);
      const unit = this.unitPrice(this.fabric, this.colour);
      const total = this.tiered ? this.price : null;
      if (this.tiered && total == null) return null;
      return {
        // Fabric-price variants are the whole seat: the structure is what remains without the
        // fabric and the paid finishes.
        structure: this.tiered ? total - units * unit - this.supplementCents : variant.price,
        supplements: this.supplements.filter((sup) => this.supplementOn.has(sup.nom)),
        eco: Math.round((Number(spec.eco) || 0) * 100),
        metres: units / 10,
        perMetre: unit * 10,
        fabric: units * unit,
        units,
        patterned: this.isPatterned(this.fabric) && Boolean(Number(spec.metrage_motif)),
        delivery: spec.livraison,
      };
    }

    /** "Livraison : 240 €" / "sur devis" (exact-price products). */
    deliveryText(spec = this.specFor(this.size)) {
      const value = spec.livraison;
      if (value == null || value === '') return '';
      if (typeof value === 'number') return `Livraison en France continentale : ${this.money.format(value)}`;
      return `Livraison : ${String(value).toLowerCase()}`;
    }

    /** Pick a size: keep the second option if it exists for it, else the first that does. */
    chooseSize(size) {
      let variante = this.variante;
      if (variante && !this.exists(size, variante)) variante = this.variantValues.find((value) => this.exists(size, value));
      this.setPicker([size, variante, this.fabricPickerValue]);
    }

    /** Pick a second-option value: keep the size if it exists in it, else the nearest that does. */
    chooseVariante(variante) {
      let size = this.size;
      if (!this.exists(size, variante)) {
        const index = this.data.sizes.indexOf(size);
        const candidates = this.data.sizes
          .map((value, i) => ({ value, distance: Math.abs(i - index) }))
          .filter(({ value }) => this.exists(value, variante))
          .sort((a, b) => a.distance - b.distance);
        size = candidates[0]?.value || size;
      }
      this.setPicker([size, variante, this.fabricPickerValue]);
    }

    /** Size data, with the second option's own values (depth, metres, code…) merged in. */
    specFor(size, variante = this.variante) {
      const base = this.specs[size] || {};
      const own = base.variantes || base.profondeurs;
      const byVariante = variante && own ? own[variante] || {} : {};
      return { ...base, ...byVariante };
    }

    sizeLabel(size) {
      return this.specFor(size).libelle || size;
    }

    /**
     * Title, value and detail of a second-option value. The raw value is only repeated when it
     * is a measure ("88 cm"): descriptive values ("Fixe, passepoil") are already in the label.
     */
    variantParts(variante) {
      const entry = this.variantLabels[variante] || {};
      const title = entry.libelle || variante;
      const repeat = title.toLowerCase() !== String(variante).toLowerCase() && /\d/.test(variante);
      return { title, value: repeat ? variante : '', detail: entry.detail || '' };
    }

    /** Illustration of a second-option value (depth profile, armrest, version, finish). */
    variantArt(variante) {
      const kind = normalize(this.variantOption);
      const entry = this.variantLabels[variante] || {};
      const spec = this.specFor(this.size, variante);
      if (kind.startsWith('profondeur')) {
        const depth = cmIn(variante) || Number(spec.profondeur) || 90;
        const seat = cmIn(entry.detail) || Number(spec.profondeur_assise) || depth - 32;
        const span = Math.max(...this.variantValues.map((value) => cmIn(value) || 0), depth);
        return profileArt({ depth, seat, span });
      }
      if (kind.startsWith('accoudoir')) {
        const width = cmIn(entry.detail) || Number(spec.largeur_accoudoir) || 10;
        return armArt(width, /rond/.test(normalize(`${variante} ${entry.libelle || ''}`)));
      }
      if (kind.startsWith('version')) {
        const depth = Number(spec.profondeur) || 90;
        const beds = this.variantValues.map((value) => Number(this.specFor(this.size, value).profondeur_depliee) || 0);
        const bed = /convertible|lit/.test(normalize(variante)) ? Number(spec.profondeur_depliee) || Math.max(...beds) : 0;
        return profileArt({
          depth,
          seat: Number(spec.profondeur_assise) || depth - 36,
          span: Math.max(depth, ...beds),
          bed,
          arrow: false,
        });
      }
      if (kind.startsWith('finition')) return finishArt(variante, this.config.type === 'banquette');
      return '';
    }

    /** Illustration of a free choice value (feet, plinth, backrest, seat, cover, side). */
    choiceArt(choice, value) {
      const kind = normalize(choice.nom);
      if (kind.startsWith('pieds')) return feetArt(value, false);
      if (kind.startsWith('socle')) return feetArt(value, true);
      if (kind.startsWith('dossier')) return backArt(value);
      if (kind.startsWith('assise')) return seatArt(value);
      if (kind.startsWith('housse')) return coverArt(value);
      if (kind.startsWith('cote')) return planSvg(this.specFor(this.size), '', /droite/.test(normalize(value)) ? 'right' : 'left');
      return '';
    }

    /** The answer to one question of a two-question option ("Fixe, passepoil" → "Fixe" / "passepoil"). */
    axisPart(variante, index) {
      return String(variante || '').split(', ')[index];
    }

    axisCombination(index, value) {
      const parts = String(this.variante || '').split(', ');
      parts[index] = value;
      return parts.join(', ');
    }

    /** Illustration of one answer: the cover (fixed / removable) or the seams. */
    axisArt(axis, value) {
      const kind = normalize(axis.nom);
      const long = this.config.type === 'banquette';
      if (kind.startsWith('housse')) return blockArt({ cover: /dehoussable/.test(normalize(value)) ? 'dehoussable' : 'fixe', long });
      if (kind.startsWith('couture') || kind.startsWith('finition')) return blockArt({ seams: seamKind(value), long });
      return '';
    }

    variantLabel(variante) {
      if (!this.variantLabels[variante]) return variante;
      const { title, value, detail } = this.variantParts(variante);
      return [title, value || detail].filter(Boolean).join(' · ');
    }

    get modelName() {
      return modelName(this.data.title);
    }

    /** Heading of a size among others of another kind ('' = the usual kind: "Deux accoudoirs" or "Canapés"). */
    sizeGroup(spec) {
      if (spec.type === 'chauffeuse') return 'Chauffeuses, sans accoudoirs';
      if (spec.plan === 'chauffeuse') return 'Sans accoudoirs';
      if (spec.plan === 'manchette') return 'Un accoudoir';
      if (spec.type === 'module') return 'Angle';
      if (spec.plan === 'table') return 'Avec table basse';
      return '';
    }

    /** "canapé", "fauteuil", "pouf"… of the selected size. */
    get noun() {
      return TYPE_NOUNS[this.specFor(this.size).type || this.config.type] || 'canapé';
    }

    /** The choices that apply to the selected size. */
    get activeChoices() {
      const size = this.size;
      return this.choices.filter((choice) => !choice.tailles || choice.tailles.includes(size));
    }

    choiceValue(choice) {
      const value = this.picked[choice.nom];
      return choice.valeurs.includes(value) ? value : choice.valeurs[0];
    }

    /** Left or right, from a "Côté…" choice (one-arm modules, méridiennes). */
    get side() {
      const choice = this.activeChoices.find((entry) => /^côté/i.test(entry.nom));
      return choice && /droite/i.test(this.choiceValue(choice)) ? 'right' : 'left';
    }

    dimensionsText(spec) {
      const overall = [spec.largeur && `L ${formatNumber(spec.largeur, spec.largeur % 1 ? 1 : 0)}`, spec.profondeur && `P ${formatNumber(spec.profondeur, spec.profondeur % 1 ? 1 : 0)}`, spec.hauteur && `H ${formatNumber(spec.hauteur, spec.hauteur % 1 ? 1 : 0)}`]
        .filter(Boolean)
        .join(' × ');
      return overall ? `${overall} cm` : '';
    }

    /* ---------- Fabrics ---------- */

    categoryFor(fabric) {
      if (this.exact) return (PRICE_BANDS.find((band) => Number(fabric.p) <= band.max) || PRICE_BANDS[PRICE_BANDS.length - 1]).label;
      if (fabric.k) return fabric.k.length === 1 ? `Catégorie ${fabric.k.toUpperCase()}` : fabric.k;
      for (const band of this.config.categories || []) {
        if (band.prix_m_max == null || fabric.p <= band.prix_m_max) return band.nom;
      }
      return null;
    }

    useRank(fabric) {
      return fabric.use ? SEAT_USE_ORDER.indexOf(fabric.use) : 0;
    }

    /** Warning shown with a fabric that is not for every seat (and the team's note). */
    useWarning(fabric) {
      return [SEAT_USES[fabric?.use]?.warning, fabric?.note].filter(Boolean).join(' · ');
    }

    loadFabrics() {
      if (!this.fabricsPromise) {
        // No "Accept: application/json" header: with it Shopify ignores ?view= and returns
        // its own collection JSON (no fabrics).
        this.fabricsPromise = fetch(this.data.fabricsUrl)
          .then((response) => {
            if (!response.ok) throw new Error(`fabrics ${response.status}`);
            return response.json();
          })
          .then(({ fabrics = [] }) => {
            const minAbrasion = Number(this.config.abrasion_min) || 0;
            this.fabrics = fabrics
              .map((raw) => {
                const category = this.categoryFor(raw);
                const families = asList(raw.f).map((value) => FAMILY_LABELS[normalize(value).trim()] || capitalize(value));
                const materials = asList(raw.m).map((value) => {
                  const text = String(value).trim().toLowerCase();
                  return MATERIAL_LABELS[text] || capitalize(text);
                });
                const fabric = {
                  handle: raw.h,
                  title: raw.t,
                  url: raw.u,
                  pricePerMetre: raw.p,
                  category,
                  families: Array.from(new Set(families)),
                  materials: Array.from(new Set(materials)),
                  composition: raw.c || '',
                  abrasion: Number(raw.a) || 0,
                  use: SEAT_USES[raw.r] ? raw.r : null,
                  note: raw.n || '',
                  colours: (raw.v || [])
                    // Exact-price products put the colour itself in the cart: sold-out colours are left out.
                    .filter((colour) => !this.exact || colour.ok !== false)
                    .map((colour) => ({
                      id: colour.i,
                      label: colourLabel(colour.l, raw.h),
                      sku: colour.s,
                      image: colour.img,
                      family: colourFamily(colour.l, colour.hex),
                      // Price of 10 cm in cents (the colour's own price; the fabric's per-metre price otherwise).
                      unitPrice: Number(colour.e) || Math.round(Number(raw.p) * 10),
                    })),
                };
                // Fabric-price variants: only the colours whose price per metre has a variant.
                if (this.tiered) fabric.colours = fabric.colours.filter((colour) => this.tierValues.has(this.tierLabel(fabric, colour)));
                return fabric;
              })
              .filter(
                (fabric) =>
                  fabric.category &&
                  this.categories.includes(fabric.category) &&
                  fabric.colours.length &&
                  // The team's recommendation, when set, is the rule (it already accounts for wear).
                  !(minAbrasion && !fabric.use && fabric.abrasion && fabric.abrasion < minAbrasion)
              )
              .sort((a, b) => this.useRank(a) - this.useRank(b) || a.title.localeCompare(b.title, 'fr'));
            this.restoreSelection();
            this.renderFabricSlot();
            if (this.exact) {
              // "dès …" prices need the cheapest fabric.
              this.renderSizes();
              this.renderRecap();
              this.updatePagePrice();
            }
            if (this.dialog?.open) this.renderBrowser();
            return this.fabrics;
          })
          .catch((error) => {
            this.fabricsPromise = null;
            throw error;
          });
      }
      return this.fabricsPromise;
    }

    /* ---------- Rendering ---------- */

    render() {
      const uid = this.data.productId;
      const hasModels = this.models.length > 1;
      const single = this.data.sizes.length === 1;
      let step = 0;
      const head = (id, title) => {
        step += 1;
        return `
          <h2 class="tdm-config__head" id="tdm-config-${id}-${uid}">
            <span class="tdm-config__num" aria-hidden="true">${step}</span>
            <span class="tdm-config__title" data-${id}-title>${title}</span>
            <span class="tdm-config__value" data-${id}-name></span>
          </h2>`;
      };

      const current = this.models.find((model) => model.current) || { title: this.data.title, image: this.data.image };
      // The same model in the other seat types (Chicago: canapé, fauteuil, pouf), in the tabs' order.
      const order = MODEL_TABS.map(([type]) => type);
      const siblings = this.models
        .filter((model) => !model.current && modelName(model.title) === this.modelName)
        .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
      const siblingLinks = siblings.length
        ? `<p class="tdm-config__siblings">${escapeHtml(this.modelName)} existe aussi en
            ${siblings
              .map((model) => `<a class="tdm-config__sibling" href="${escapeHtml(model.url)}">${escapeHtml(TYPE_SINGULAR[model.type] || model.label || model.title)}</a>`)
              .join('')}</p>`
        : '';
      const modelsStep = hasModels
        ? `
        <section class="tdm-config__step" aria-labelledby="tdm-config-model-${uid}">
          ${head('model', 'Modèle')}
          <div class="tdm-config__model-current">
            ${image(current.image, 'tdm-config__model-thumb', 160)}
            <div class="tdm-config__model-text">
              <strong>${escapeHtml(this.modelName)}</strong>
              <span>${current.sizes ? `${current.sizes} dimension${current.sizes > 1 ? 's' : ''}` : ''}</span>
            </div>
            <button type="button" class="tdm-config__button" data-models-open>Voir les ${this.models.length} modèles</button>
          </div>
          ${siblingLinks}
        </section>`
        : '';

      const variantArts = this.variantValues.map((value) => this.variantArt(value));
      const withArt = variantArts.some(Boolean);
      const axisControls = this.axes
        ? this.axes
            .map(
              (axis, axisIndex) => `
          <div class="tdm-config__depths" role="radiogroup" aria-label="${escapeHtml(axis.nom)}">
            <span class="tdm-config__depths-label">${escapeHtml(axis.nom)}</span>
            <div class="tdm-config__depths-options tdm-config__depths-options--art">
              ${axis.valeurs
                .map((entry) => {
                  const drawing = this.axisArt(axis, entry.valeur);
                  return `
                  <label class="tdm-config__depth">
                    <input class="tdm-config__input" type="radio" name="tdm-axis-${uid}-${axisIndex}" value="${escapeHtml(entry.valeur)}"
                      data-axis-input data-axis="${axisIndex}">
                    ${drawing ? `<span class="tdm-config__depth-art">${drawing}</span>` : ''}
                    <strong>${escapeHtml(entry.libelle || entry.valeur)}</strong>
                    ${entry.detail ? `<span>${escapeHtml(entry.detail)}</span>` : ''}
                  </label>`;
                })
                .join('')}
            </div>
          </div>`
            )
            .join('')
        : '';
      const variantControl = this.axes
        ? axisControls
        : this.variantValues.length
        ? `
          <div class="tdm-config__depths" role="radiogroup" aria-label="${escapeHtml(this.variantOption)}">
            <span class="tdm-config__depths-label">${escapeHtml(this.variantOption === 'Profondeur' ? 'Profondeur d’assise' : this.variantOption)}</span>
            <div class="tdm-config__depths-options${this.variantValues.length > 3 ? ' tdm-config__depths-options--grid' : ''}${withArt ? ' tdm-config__depths-options--art' : ''}">
              ${this.variantValues
                .map((value, index) => {
                  const parts = this.variantParts(value);
                  const detail = [parts.value, parts.detail].filter(Boolean).join(' · ');
                  return `
                  <label class="tdm-config__depth">
                    <input class="tdm-config__input" type="radio" name="tdm-variant-${uid}" value="${index}" data-variant-input>
                    ${variantArts[index] ? `<span class="tdm-config__depth-art">${variantArts[index]}</span>` : ''}
                    <strong>${escapeHtml(parts.title)}</strong>
                    ${detail ? `<span>${escapeHtml(detail)}</span>` : ''}
                    <span class="tdm-config__depth-price" data-variant-price="${index}" hidden></span>
                  </label>`;
                })
                .join('')}
            </div>
          </div>`
        : '';

      const groups = this.data.sizes.map((size) => this.sizeGroup(this.specFor(size)));
      const grouped = new Set(groups).size > 1;
      const usualGroup = groups.some((group) => /accoudoir/i.test(group)) ? 'Deux accoudoirs' : 'Canapés';
      const sizeCards = single
        ? ''
        : `
          <div class="tdm-config__sizes" role="radiogroup" aria-labelledby="tdm-config-size-${uid}">
            ${this.data.sizes
              .map((size, index) => {
                const spec = this.specFor(size);
                const heading =
                  grouped && groups[index] !== groups[index - 1]
                    ? `<p class="tdm-config__size-group">${escapeHtml(groups[index] || usualGroup)}</p>`
                    : '';
                return `${heading}
                <label class="tdm-config__size" data-size-card="${index}">
                  <input class="tdm-config__input" type="radio" name="tdm-size-${uid}" value="${index}" data-size-input>
                  <span class="tdm-config__size-plan" data-size-plan="${index}">${planSvg(spec)}</span>
                  <span class="tdm-config__size-name">${escapeHtml(this.sizeLabel(size))}</span>
                  <span class="tdm-config__size-meta">
                    <span data-size-width="${index}">${spec.largeur ? `${escapeHtml(spec.largeur)} cm` : ''}</span>
                    <span class="tdm-config__size-price" data-size-price="${index}"></span>
                  </span>
                </label>`;
              })
              .join('')}
          </div>`;

      const modular = this.config.modulaire
        ? `<p class="tdm-config__info">${ICONS.info}<span>Canapé modulable : commandez-le module par module (le tissu choisi reste le même), ou
            <button type="button" class="tdm-config__inline-link" data-quote data-quote-option="Composition sur plan">demandez un plan de composition</button>.</span></p>`
        : '';

      const choicesStep = this.choices.length
        ? `
        <section class="tdm-config__step" aria-labelledby="tdm-config-choice-${uid}" data-choices-step>
          ${head('choice', 'Finitions')}
          <div class="tdm-config__choices" data-choices></div>
        </section>`
        : '';

      this.innerHTML = `
        <div class="tdm-config__banner" data-banner role="status" hidden></div>

        ${modelsStep}

        <section class="tdm-config__step" aria-labelledby="tdm-config-size-${uid}">
          ${head('size', single ? 'Dimensions' : 'Dimension')}
          ${modular}
          ${sizeCards}
          ${variantControl}
          <p class="tdm-config__dims" data-dims></p>
        </section>

        ${choicesStep}

        <section class="tdm-config__step" aria-labelledby="tdm-config-fabric-${uid}">
          ${head('fabric', 'Tissu')}
          <div data-fabric-slot></div>
          <p class="tdm-config__error" data-error role="alert" hidden></p>
        </section>

        <section class="tdm-config__step" aria-labelledby="tdm-config-recap-${uid}">
          ${head('recap', 'Votre canapé')}
          <div data-recap></div>
        </section>

        ${this.browserMarkup(uid)}
        ${this.modelsMarkup(uid)}

        <dialog class="tdm-sheet tdm-sheet--review" data-review aria-labelledby="tdm-review-title-${uid}">
          <div class="tdm-sheet__panel" data-review-body></div>
        </dialog>

        <dialog class="tdm-sheet tdm-sheet--quote" data-quote-dialog aria-labelledby="tdm-quote-title-${uid}">
          <div class="tdm-sheet__panel">
            <header class="tdm-sheet__header">
              <div>
                <p class="tdm-sheet__eyebrow">${escapeHtml(this.modelName)} · sur mesure</p>
                <h2 class="tdm-sheet__title" id="tdm-quote-title-${uid}">Demander un devis</h2>
              </div>
              <button type="button" class="tdm-sheet__close" data-close aria-label="Fermer">${ICONS.close}</button>
            </header>
            <div class="tdm-sheet__body">
              <p class="tdm-quote__intro">Une autre dimension, un tissu qui n’est pas proposé, une option, une question sur
                le confort ou la livraison ? Notre service Sièges et Canapés vous répond avec un devis personnalisé.</p>
              <div class="tdm-quote__config" data-quote-config></div>
              <div data-quote-form></div>
            </div>
          </div>
        </dialog>
      `;

      this.dialog = this.querySelector('[data-dialog]');
      this.review = this.querySelector('[data-review]');
      this.quoteDialog = this.querySelector('[data-quote-dialog]');
      this.modelsDialog = this.querySelector('[data-models-dialog]');
      if (this.quoteTemplate) {
        this.querySelector('[data-quote-form]').append(this.quoteTemplate.content.cloneNode(true));
      }

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-size-input]')) this.chooseSize(this.data.sizes[Number(target.value)]);
        if (target.matches('[data-variant-input]')) this.chooseVariante(this.variantValues[Number(target.value)]);
        if (target.matches('[data-axis-input]')) this.chooseVariante(this.axisCombination(Number(target.dataset.axis), target.value));
        if (target.matches('[data-supplement-input]')) {
          if (target.value === '1') this.supplementOn.add(target.dataset.supplement);
          else this.supplementOn.delete(target.dataset.supplement);
          this.setPicker([this.variante]);
          this.renderChoices();
          this.renderSizes();
          this.renderRecap();
          this.updatePagePrice();
        }
        if (target.matches('[data-extra-input]')) {
          if (target.checked) this.extraOn.add(target.value);
          else this.extraOn.delete(target.value);
          this.renderRecap();
          this.updatePagePrice();
        }
        if (target.matches('[data-choice-input]')) {
          this.picked[target.dataset.choice] = target.value;
          this.renderChoices();
          this.renderSizes();
          this.renderRecap();
          this.syncProperties();
        }
        if (target.matches('[data-sort]')) {
          this.sort = target.value;
          this.renderResults();
        }
      });

      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-search]')) return;
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.query = normalize(event.target.value).trim();
          this.renderResults();
        }, 150);
      });

      this.addEventListener('click', (event) => {
        // A click on the backdrop (outside the panel) closes a dialog.
        if (event.target instanceof HTMLDialogElement) {
          event.target.close();
          return;
        }
        const target = event.target.closest('button, a');
        if (!target || !this.contains(target)) return;
        // detail 0: the click came from the keyboard (Enter / Space).
        this.keyboardClick = event.detail === 0;
        if (target.matches('[data-open]')) this.openBrowser();
        else if (target.matches('[data-close]')) target.closest('dialog')?.close();
        else if (target.matches('[data-sample]')) this.addSample(target);
        else if (target.matches('[data-group]')) this.toggleGroup(target.dataset.group);
        else if (target.matches('[data-filter]')) this.toggleFilter(target.dataset.filter, target.dataset.value);
        else if (target.matches('[data-reset]')) this.resetFilters();
        else if (target.matches('[data-pick]')) this.pick(target.dataset.pick);
        else if (target.matches('[data-commit]')) this.commit();
        else if (target.matches('[data-quote]')) this.openQuote(target.dataset.quoteOption || '');
        else if (target.matches('[data-review-confirm]')) this.confirmReview();
        else if (target.matches('[data-review-edit]')) this.review.close();
        else if (target.matches('[data-models-open]')) this.openModels();
        else if (target.matches('[data-model-tab]')) {
          this.modelTab = target.dataset.modelTab;
          this.renderModels();
          this.querySelector(`[data-model-tab="${this.modelTab}"]`)?.focus();
        }
      });

      this.querySelector('[data-quote-form] form')?.addEventListener('submit', () => this.fillQuote());

      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('close', () => {
          if (!this.querySelector('dialog[open]')) document.documentElement.classList.remove('tdm-fabrics-open');
        });
      });
    }

    browserMarkup(uid) {
      return `
        <dialog class="tdm-fabrics" data-dialog aria-labelledby="tdm-fabrics-title-${uid}">
          <div class="tdm-fabrics__panel">
            <header class="tdm-fabrics__header">
              <div>
                <p class="tdm-fabrics__eyebrow" data-dialog-eyebrow></p>
                <h2 class="tdm-fabrics__title" id="tdm-fabrics-title-${uid}">Choisir un tissu</h2>
              </div>
              <button type="button" class="tdm-fabrics__close" data-close aria-label="Fermer">${ICONS.close}</button>
            </header>
            <div class="tdm-fabrics__tools">
              <label class="tdm-fabrics__search">
                ${ICONS.search}
                <span class="visually-hidden">Rechercher</span>
                <input type="search" data-search placeholder="Rechercher un tissu, un coloris…" autocomplete="off">
              </label>
              <div class="tdm-fabrics__filters" data-filters></div>
            </div>
            <div class="tdm-fabrics__bar">
              <p class="tdm-fabrics__count" data-count aria-live="polite"></p>
              <button type="button" class="tdm-fabrics__reset" data-reset hidden aria-label="Effacer les filtres">Effacer</button>
              <label class="tdm-fabrics__sort">
                <span class="visually-hidden">Trier</span>
                <select data-sort>
                  ${SORTS.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')}
                </select>
              </label>
            </div>
            <div class="tdm-fabrics__results" data-results></div>
            <footer class="tdm-fabrics__footer" data-footer></footer>
          </div>
        </dialog>`;
    }

    modelsMarkup(uid) {
      if (this.models.length < 2) return '';
      return `
        <dialog class="tdm-sheet tdm-sheet--models" data-models-dialog aria-labelledby="tdm-models-title-${uid}">
          <div class="tdm-sheet__panel">
            <header class="tdm-sheet__header">
              <div>
                <p class="tdm-sheet__eyebrow">Sur mesure</p>
                <h2 class="tdm-sheet__title" id="tdm-models-title-${uid}">Nos modèles</h2>
              </div>
              <button type="button" class="tdm-sheet__close" data-close aria-label="Fermer">${ICONS.close}</button>
            </header>
            <div class="tdm-sheet__body" data-models-body></div>
          </div>
        </dialog>`;
    }

    openDialog(dialog) {
      document.documentElement.classList.add('tdm-fabrics-open');
      if (!dialog.open) dialog.showModal();
    }

    openModels() {
      this.renderModels();
      this.openDialog(this.modelsDialog);
    }

    renderModels() {
      const body = this.querySelector('[data-models-body]');
      if (!body) return;
      // A model shows under its product type and under every seat type it is tagged with
      // (Chicago: Canapés, Fauteuils, Poufs).
      const is = (model, type) => model.type === type || (model.tags || []).some((tag) => tag.toLowerCase() === type);
      const tabs = MODEL_TABS.filter(([type]) => this.models.some((model) => is(model, type)));
      const shown = this.models.filter((model) => !this.modelTab || is(model, this.modelTab));
      body.innerHTML = `
        <div class="tdm-models__tabs" role="group" aria-label="Type de siège">
          <button type="button" class="tdm-fabrics__chip" data-model-tab="" aria-pressed="${!this.modelTab}">Tous <span>${this.models.length}</span></button>
          ${tabs
            .map(([type, label]) => {
              const count = this.models.filter((model) => is(model, type)).length;
              return `<button type="button" class="tdm-fabrics__chip" data-model-tab="${type}" aria-pressed="${this.modelTab === type}">${label} <span>${count}</span></button>`;
            })
            .join('')}
        </div>
        <div class="tdm-models__grid">
          ${shown
            .map((model) => {
              const inner = `
                <span class="tdm-models__media">${image(model.image, 'tdm-models__image', 320)}</span>
                <span class="tdm-models__name">${escapeHtml(modelName(model.title))}</span>
                <span class="tdm-models__meta">${escapeHtml(model.label || '')}${model.sizes ? ` · ${model.sizes} dimension${model.sizes > 1 ? 's' : ''}` : ''}</span>`;
              return model.current
                ? `<span class="tdm-models__card is-current" aria-current="true">${inner}</span>`
                : `<a class="tdm-models__card" href="${escapeHtml(model.url)}">${inner}</a>`;
            })
            .join('')}
        </div>`;
    }

    renderSizes() {
      const size = this.size;
      const variante = this.variante;
      const sizeIndex = this.data.sizes.indexOf(size);
      this.querySelectorAll('[data-size-input]').forEach((input) => {
        const index = Number(input.value);
        const value = this.data.sizes[index];
        const available = this.exists(value, variante);
        input.checked = index === sizeIndex;
        input.disabled = !available;
        input.closest('.tdm-config__size')?.classList.toggle('is-unavailable', !available);
      });
      // Price difference of each second-option value for this size (from the cheapest one).
      const structures = this.tiered ? this.variantValues.map((value) => this.structurePrice(size, value)) : [];
      const cheapest = Math.min(...structures.filter((value) => value != null));
      this.querySelectorAll('[data-variant-input]').forEach((input) => {
        const value = this.variantValues[Number(input.value)];
        input.checked = value === variante;
        // A value the selected size does not exist in stays clickable: the size follows.
        input.closest('.tdm-config__depth')?.classList.toggle('is-other', !this.exists(size, value));
        const label = this.querySelector(`[data-variant-price="${input.value}"]`);
        if (label) {
          const difference = structures[Number(input.value)] != null ? structures[Number(input.value)] - cheapest : 0;
          label.textContent = difference > 0 ? `+ ${this.money.format(difference / 100)}` : '';
          label.hidden = !(difference > 0);
        }
      });
      this.querySelectorAll('[data-axis-input]').forEach((input) => {
        const index = Number(input.dataset.axis);
        input.checked = this.axisPart(variante, index) === input.value;
        input.closest('.tdm-config__depth')?.classList.toggle('is-other', !this.exists(size, this.axisCombination(index, input.value)));
      });
      this.querySelector('[data-size-name]').textContent = [this.sizeLabel(size), variante && this.variantLabel(variante)]
        .filter(Boolean)
        .join(' · ');

      this.data.sizes.forEach((entry, index) => {
        const target = this.querySelector(`[data-size-price="${index}"]`);
        const available = this.exists(entry, variante);
        if (target) {
          if (!available) {
            target.textContent = this.variantValues.length ? `Pas en ${variante}` : '';
          } else {
            const price = this.fabric ? this.priceFor(entry, this.fabric, variante, this.colour) : this.fromPrice(entry);
            target.textContent = price == null ? '' : `${this.fabric ? '' : 'dès '}${this.money.format(price / 100)}`;
          }
        }
        // Plans and widths follow the second option (depth, armrests).
        const plan = this.querySelector(`[data-size-plan="${index}"]`);
        if (plan && this.variantValues.length) plan.innerHTML = planSvg(this.specFor(entry), '', entry === size ? this.side : 'left');
        else if (plan && entry === size) plan.innerHTML = planSvg(this.specFor(entry), '', this.side);
        const widthLabel = this.querySelector(`[data-size-width="${index}"]`);
        const spec = this.specFor(entry);
        if (widthLabel) widthLabel.textContent = spec.largeur ? `${formatNumber(spec.largeur, spec.largeur % 1 ? 1 : 0)} cm` : '';
      });

      const spec = this.specFor(size);
      const parts = [
        this.dimensionsText(spec),
        spec.hauteur_assise && `assise à ${spec.hauteur_assise} cm`,
        spec.profondeur_assise && `profondeur d’assise ${spec.profondeur_assise} cm`,
        spec.couchage && `couchage ${spec.couchage}`,
        spec.profondeur_depliee && `déplié ${spec.profondeur_depliee} cm`,
        spec.note,
      ].filter(Boolean);
      const dims = this.querySelector('[data-dims]');
      dims.textContent = parts.join(' · ');
      dims.hidden = !parts.length;

      const recapTitle = this.querySelector('[data-recap-title]');
      if (recapTitle) recapTitle.textContent = `Votre ${this.noun}`;
    }

    renderChoices() {
      const target = this.querySelector('[data-choices]');
      const step = this.querySelector('[data-choices-step]');
      if (!target) return;
      const choices = this.activeChoices;
      step.hidden = !choices.length && !this.supplements.length;
      const uid = this.data.productId;
      const supplementSets = this.supplements
        .map(
          (sup, index) => `
          <fieldset class="tdm-config__choice">
            <legend class="tdm-config__choice-label">${escapeHtml(sup.nom)}</legend>
            <div class="tdm-config__choice-options">
              ${[
                ['0', 'Non'],
                ['1', `Oui · + ${this.money.format(sup.prix)}`],
              ]
                .map(
                  ([value, label]) => `
                <label class="tdm-config__choice-option">
                  <input class="tdm-config__input" type="radio" name="tdm-supplement-${uid}-${index}" value="${value}"
                    data-supplement-input data-supplement="${escapeHtml(sup.nom)}" ${(value === '1') === this.supplementOn.has(sup.nom) ? 'checked' : ''}>
                  <span><span class="tdm-config__choice-text">${escapeHtml(label)}</span></span>
                </label>`
                )
                .join('')}
            </div>
            ${sup.detail ? `<p class="tdm-config__choice-detail">${escapeHtml(sup.detail)}</p>` : ''}
          </fieldset>`
        )
        .join('');
      target.innerHTML = supplementSets + choices
        .map((choice, index) => {
          const current = this.choiceValue(choice);
          return `
          <fieldset class="tdm-config__choice">
            <legend class="tdm-config__choice-label">${escapeHtml(choice.nom)}</legend>
            <div class="tdm-config__choice-options">
              ${choice.valeurs
                .map((value) => {
                  const drawing = this.choiceArt(choice, value);
                  return `
                <label class="tdm-config__choice-option${drawing ? ' tdm-config__choice-option--art' : ''}">
                  <input class="tdm-config__input" type="radio" name="tdm-choice-${uid}-${index}" value="${escapeHtml(value)}"
                    data-choice-input data-choice="${escapeHtml(choice.nom)}" ${value === current ? 'checked' : ''}>
                  <span>${drawing}<span class="tdm-config__choice-text">${escapeHtml(value)}</span></span>
                </label>`;
                })
                .join('')}
            </div>
          </fieldset>`;
        })
        .join('');
      this.querySelector('[data-choice-name]').textContent = [
        ...this.supplements.filter((sup) => this.supplementOn.has(sup.nom)).map((sup) => sup.nom),
        ...choices.map((choice) => this.choiceValue(choice)),
      ].join(' · ');
    }

    renderFabricSlot() {
      const slot = this.querySelector('[data-fabric-slot]');
      const name = this.querySelector('[data-fabric-name]');
      const { fabric, colour } = this;

      if (fabric && colour) {
        name.textContent = `${fabric.title} · ${colour.label}`;
        const tags = [...fabric.families, fabric.materials.join(', ')].filter(Boolean).join(' · ');
        const specs = [fabric.composition, fabric.abrasion && `${formatNumber(fabric.abrasion)} cycles Martindale`]
          .filter(Boolean)
          .join(' · ');
        this.replaceKeepingFocus(slot, `
          <div class="tdm-config__chosen">
            ${image(colour.image, 'tdm-config__chosen-swatch', 240)}
            <div class="tdm-config__chosen-text">
              ${tags ? `<p class="tdm-config__chosen-eyebrow">${escapeHtml(tags)}</p>` : ''}
              <p class="tdm-config__chosen-name">${escapeHtml(fabric.title)} <span>${escapeHtml(colour.label)}</span></p>
              ${specs ? `<p class="tdm-config__chosen-specs">${escapeHtml(specs)}</p>` : ''}
              ${this.useWarning(fabric) ? `<p class="tdm-config__warning">${ICONS.info}<span>${escapeHtml(this.useWarning(fabric))}</span></p>` : ''}
              <a class="tdm-config__link" href="${escapeHtml(fabric.url)}" target="_blank" rel="noopener">Voir la fiche du tissu</a>
            </div>
            <div class="tdm-config__chosen-actions">
              <button type="button" class="tdm-config__button" data-open>Changer de tissu</button>
              <button type="button" class="tdm-config__sample" data-sample aria-label="Recevoir un échantillon de ce coloris">${ICONS.scissors}<span>Échantillon</span></button>
            </div>
          </div>`);
        return;
      }

      name.textContent = '';
      const fabrics = this.fabrics || [];
      const colourCount = fabrics.reduce((sum, entry) => sum + entry.colours.length, 0);
      const mosaic = fabrics.length
        ? this.mosaic(fabrics).map((url) => image(url, 'tdm-config__browse-swatch', 80)).join('')
        : '<span class="tdm-config__browse-swatch tdm-config__browse-swatch--empty"></span>'.repeat(4);
      this.replaceKeepingFocus(slot, `
        <button type="button" class="tdm-config__browse" data-open>
          <span class="tdm-config__browse-mosaic" aria-hidden="true">${mosaic}</span>
          <span class="tdm-config__browse-text">
            <strong>Choisir mon tissu</strong>
            <span>${
              fabrics.length
                ? `${fabrics.length} tissus · ${colourCount} coloris, à filtrer par couleur, aspect et matière`
                : 'Unis, velours, lin, laine… à filtrer par couleur, aspect et matière'
            }</span>
          </span>
          <span class="tdm-config__browse-icon" aria-hidden="true">${ICONS.chevron}</span>
        </button>`);
    }

    /** Redraw `target` with `html`, keeping the focus on the same control (by data attribute). */
    replaceKeepingFocus(target, html) {
      const active = document.activeElement;
      const key = target.contains(active) ? ['data-open', 'data-sample', 'data-quote'].find((name) => active.hasAttribute(name)) : null;
      target.innerHTML = html;
      if (key) target.querySelector(`[${key}]`)?.focus({ preventScroll: true });
    }

    /** Four swatches from different colour families for the "Choisir mon tissu" card. */
    mosaic(fabrics) {
      const picked = [];
      const seen = new Set();
      for (const fabric of fabrics) {
        for (const colour of fabric.colours) {
          if (!colour.image || seen.has(colour.family)) continue;
          seen.add(colour.family);
          picked.push(colour.image);
          break;
        }
        if (picked.length === 4) break;
      }
      return picked;
    }

    /** Rows describing the current seat, shared by the recap, the review and the quote form. */
    summaryRows() {
      const size = this.size;
      const variante = this.variante;
      const spec = this.specFor(size);
      const rows = [
        ['Modèle', this.modelName],
        ['Dimension', [this.sizeLabel(size), this.dimensionsText(spec)].filter(Boolean).join(' · ')],
      ];
      if (variante) {
        const { title, value, detail } = this.variantParts(variante);
        rows.push([this.variantOption, [title, value, detail].filter(Boolean).join(' · ')]);
      }
      if (spec.couchage) rows.push(['Couchage', spec.couchage]);
      this.supplements.forEach((sup) => {
        if (this.supplementOn.has(sup.nom)) rows.push([sup.nom, `Oui · + ${this.money.format(sup.prix)}`]);
      });
      this.activeChoices.forEach((choice) => rows.push([choice.nom, this.choiceValue(choice)]));
      rows.push(['Tissu', this.fabric && this.colour ? `${this.fabric.title}, coloris ${this.colour.label}` : 'À choisir']);
      return rows;
    }

    get price() {
      return this.fabric ? this.priceFor(this.size, this.fabric, this.variante, this.colour) : null;
    }

    renderRecap() {
      const recap = this.querySelector('[data-recap]');
      const spec = this.specFor(this.size);
      const complete = this.complete;
      const price = this.total;
      const from = this.fromPrice(this.size);
      this.querySelector('[data-recap-name]').textContent = complete && price != null ? this.money.format(price / 100) : '';
      const extras = this.extras.filter((extra) => this.extraAvailable(extra));
      const extrasMarkup = extras.length
        ? `
          <div class="tdm-config__extras">
            <p class="tdm-config__on-request-title">Ajouter à votre commande</p>
            ${extras
              .map((extra) => {
                const extraPrice = this.extraPrice(extra);
                return `
                <label class="tdm-config__extra">
                  <input class="tdm-config__extra-input" type="checkbox" value="${escapeHtml(extra.id)}" data-extra-input ${this.extraOn.has(extra.id) ? 'checked' : ''}>
                  <span class="tdm-config__extra-text"><strong>${escapeHtml(extra.nom)}</strong>${extra.detail ? `<span>${escapeHtml(extra.detail)}</span>` : ''}</span>
                  <span class="tdm-config__extra-price">${extraPrice != null ? `+ ${this.money.format(extraPrice / 100)}` : 'selon le tissu'}</span>
                </label>`;
              })
              .join('')}
          </div>`
        : '';
      const notes = this.notes.length
        ? `<ul class="tdm-config__recap-notes">${this.notes.map((note) => `<li>${ICONS.info}<span>${escapeHtml(note)}</span></li>`).join('')}</ul>`
        : '';
      const quoteOptions = this.quoteOptions.length
        ? `
          <div class="tdm-config__on-request">
            <p class="tdm-config__on-request-title">Sur demande</p>
            <ul>
              ${this.quoteOptions
                .map(
                  (option) => `<li><button type="button" class="tdm-config__on-request-option" data-quote data-quote-option="${escapeHtml(option)}">${ICONS.plus}<span>${escapeHtml(option)}</span></button></li>`
                )
                .join('')}
            </ul>
          </div>`
        : '';
      this.replaceKeepingFocus(recap, `
        <div class="tdm-config__recap${complete ? ' is-complete' : ''}">
          <div class="tdm-config__recap-visual">
            ${planSvg(spec, complete ? this.colour.image : '', this.side)}
            ${complete ? '' : '<span class="tdm-config__recap-hint">Votre tissu apparaîtra ici</span>'}
          </div>
          <dl class="tdm-config__recap-list">
            ${this.summaryRows()
              .map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`)
              .join('')}
          </dl>
          <div class="tdm-config__recap-total">
            <span>${complete ? 'Prix TTC' : 'À partir de'}</span>
            <strong>${
              complete && price != null ? this.money.format(price / 100) : from != null ? this.money.format(from / 100) : ''
            }</strong>
          </div>
          ${this.breakdownMarkup('tdm-config__recap-breakdown')}
          ${
            this.leadTime
              ? `<p class="tdm-config__recap-lead">${ICONS.clock}<span><strong>Fabriqué sur commande</strong> dans nos ateliers · ${escapeHtml(
                  this.leadTime
                )}</span></p>`
              : ''
          }
          ${notes}
        </div>
        ${extrasMarkup}
        ${quoteOptions}
        <button type="button" class="tdm-config__quote" data-quote>
          ${ICONS.message}
          <span><strong>Une demande particulière ?</strong> Demander un devis ou poser une question</span>
          ${ICONS.chevron}
        </button>`);
    }

    /** "Canapé : 2 649,50 € (dont éco-participation 9,50 €)", "Tissu : 10,40 m × 98 €/m = …" and the delivery. */
    breakdownMarkup(className, quantity = 1) {
      if (!this.exact) return '';
      const parts = this.breakdown;
      const delivery = this.deliveryText();
      if (!parts) return delivery ? `<p class="${className}">${escapeHtml(delivery)}</p>` : '';
      const eco = parts.eco ? ` (dont éco-participation ${this.money.format((parts.eco * quantity) / 100)})` : '';
      const lines = [
        `${capitalize(this.noun)} : ${this.money.format((parts.structure * quantity) / 100)}${eco}`,
        ...(parts.supplements || []).map((sup) => `${sup.nom} : ${this.money.format(sup.prix * quantity)}`),
        `Tissu : ${formatNumber(parts.metres * quantity, 2)} m${parts.patterned ? ' (raccord de motif)' : ''} × ${this.money.format(
          parts.perMetre / 100
        )}/m = ${this.money.format((parts.fabric * quantity) / 100)}`,
        ...this.activeExtras.map((extra) => {
          const price = this.extraPrice(extra);
          return `${extra.nom} (ligne séparée) : ${price == null ? 'selon le tissu' : this.money.format((price * quantity) / 100)}`;
        }),
        delivery,
      ].filter(Boolean);
      return `<ul class="${className}">${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}</ul>`;
    }

    /**
     * The product page price and the sticky bar show the seat's total (exact-price products):
     * the variant alone is the structure without fabric. Pro (HT) displays are left as they are.
     */
    updatePagePrice() {
      if (!this.exact || this.section.querySelector('[data-product-price-ttc]')) return;
      const price = this.total;
      const from = this.fromPrice(this.size);
      const text = price != null ? this.money.format(price / 100) : from != null ? `dès ${this.money.format(from / 100)}` : '';
      if (!text) return;
      const main = this.section.querySelector('[data-product-price]');
      if (main) main.textContent = text;
      const sticky = document.getElementById('tdm-sticky-price');
      if (sticky) sticky.textContent = text;
    }

    /**
     * Exact-price products without fabric-price variants: the seat and its fabric go to the cart
     * in one request, grouped (`_tdm_groupe`) for a cart function. Cart functions only run for
     * custom apps on Shopify Plus, so the cart shows two lines: the products now carry a "Prix du
     * tissu" option instead (one line through the product form); kept for a product without it.
     */
    async addToCart() {
      const button = this.review.querySelector('[data-review-confirm]');
      const error = this.review.querySelector('[data-review-error]');
      const size = this.size;
      const variant = this.findVariant(size, null, this.variante);
      const parts = this.breakdown;
      if (!variant || !parts) return;
      this.syncProperties();
      const quantity = this.quantity;
      const group = `SM-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const title = `${this.data.title.replace(/\s*[—–-]\s*sur mesure.*$/i, '')} sur mesure · ${size}`;
      const properties = {};
      this.form?.querySelectorAll('input[data-tdm-config-property]').forEach((input) => {
        const key = input.name.replace(/^properties\[|\]$/g, '');
        if (input.value) properties[key] = input.value;
      });
      const details = [
        ...(this.variante && this.variantLabels[this.variante]?.libelle ? [[this.variantOption, this.variantLabel(this.variante)]] : []),
        ...this.activeChoices.map((choice) => [choice.nom, this.choiceValue(choice)]),
        ['Tissu', `${this.fabric.title}, coloris ${this.colour.label}`],
        ['Réf. tissu', this.colour.sku || ''],
        ['Métrage', `${formatNumber(parts.metres, 2)} m`],
        ['Fabrication', this.leadTime ? `Sur commande · ${this.leadTime}` : 'Sur commande'],
      ].filter(([, value]) => value);
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const body = {
        items: [
          {
            id: variant.id,
            quantity,
            properties: {
              ...properties,
              _tdm_groupe: group,
              _tdm_role: 'principal',
              _tdm_titre: title,
              _tdm_details: JSON.stringify(details),
            },
          },
          {
            id: this.colour.id,
            quantity: parts.units * quantity,
            properties: { _tdm_groupe: group, _tdm_role: 'tissu', Pour: title },
          },
        ],
        ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
      };
      button.disabled = true;
      error.hidden = true;
      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              `Le tissu ${this.fabric.title} ${this.colour.label} n’est pas disponible en ${formatNumber(parts.metres, 2)} m.`
          );
        }
        this.review.close();
        this.refreshCart(result);
      } catch (failure) {
        error.textContent = failure.message || 'L’ajout au panier a échoué, réessayez.';
        error.hidden = false;
      } finally {
        button.disabled = false;
      }
    }

    /**
     * Seat + accessories in one request: the seat as the product form would add it (its variant
     * and properties), each accessory on its own line for the same seat.
     */
    async addWithExtras() {
      const button = this.review.querySelector('[data-review-confirm]');
      const error = this.review.querySelector('[data-review-error]');
      button.disabled = true;
      error.hidden = true;
      try {
        this.syncProperties();
        const form = this.form;
        const seatId = form?.querySelector('input[name="id"]')?.value;
        // The form follows the picker after Horizon's update; the listed variants can be checked.
        const listed = this.findVariant(this.size, null, this.variante, this.fabricPickerValue);
        if (!seatId || (listed && String(listed.id) !== String(seatId))) {
          throw new Error('La configuration se met à jour, réessayez dans un instant.');
        }
        const quantity = this.quantity;
        const properties = {};
        form.querySelectorAll('input[data-tdm-config-property]').forEach((input) => {
          const key = input.name.replace(/^properties\[|\]$/g, '');
          if (input.value) properties[key] = input.value;
        });
        const pour = `${this.modelName} · ${this.sizeLabel(this.size)}`;
        const items = [{ id: Number(seatId), quantity, properties }];
        for (const extra of this.activeExtras) {
          const variant = await this.extraVariant(extra);
          if (!variant || variant.available === false) throw new Error(`${extra.nom} : indisponible pour cette configuration.`);
          const expected = this.extraPrice(extra);
          if (expected != null && Number(variant.price) !== expected) {
            throw new Error(`${extra.nom} : prix à vérifier, demandez-nous un devis.`);
          }
          const extraProperties = { Pour: pour };
          if (extra.id === 'housse2') {
            Object.assign(extraProperties, { Tissu: this.fabric.title, Coloris: this.colour.label, 'Réf. tissu': this.colour.sku || '' });
          }
          items.push({ id: Number(variant.id), quantity, properties: extraProperties });
        }
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => element.dataset.sectionId)
          .filter(Boolean);
        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}) }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'L’ajout au panier a échoué, réessayez.');
        this.review.close();
        this.refreshCart(result);
      } catch (failure) {
        error.textContent = failure.message || 'L’ajout au panier a échoué, réessayez.';
        error.hidden = false;
      } finally {
        button.disabled = false;
      }
    }

    /** Same refresh as the theme's other multi-item adds (cushion + filling): sections, events, drawer. */
    refreshCart(result) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: result,
            sourceId: 'tdm-canape-configurator',
            data: { source: 'tdm-canape-configurator', sections: result.sections },
          },
        })
      );
      if (result.sections) {
        Object.entries(result.sections).forEach(([id, html]) => {
          const target = document.querySelector(`#shopify-section-${id}`) || document.querySelector(`[data-section-id="${id}"]`);
          if (target && html) target.innerHTML = html;
        });
      }
      const drawer = document.querySelector('cart-drawer-component');
      if (typeof drawer?.open === 'function') drawer.open();
      else if (typeof drawer?.showDialog === 'function') drawer.showDialog();
    }

    /** Refresh everything that depends on the selected size, second option or variant. */
    update() {
      this.renderSizes();
      this.renderChoices();
      // Steps are numbered in order of the visible ones (the "Finitions" step can be hidden).
      this.querySelectorAll('.tdm-config__step:not([hidden]) .tdm-config__num').forEach((num, index) => {
        num.textContent = String(index + 1);
      });
      this.renderFabricSlot();
      this.renderRecap();
      if (!this.exact && this.fabric && this.category !== this.fabric.category) this.setPicker([this.fabric.category]);
      if (this.tiered && this.complete && this.pickerValue([...this.tierValues]) !== this.fabricPickerValue) {
        this.setPicker([this.fabricPickerValue]);
      }
      this.updatePagePrice();
      if (this.dialog?.open) this.renderBrowser();
      this.syncProperties();
    }

    /* ---------- Fabric browser ---------- */

    openBrowser() {
      this.pending = this.fabric && this.colour ? { fabric: this.fabric, colour: this.colour } : null;
      this.openDialog(this.dialog);
      this.renderBrowser();
      if (!this.fabrics) {
        this.querySelector('[data-results]').innerHTML = '<p class="tdm-fabrics__empty">Chargement des tissus…</p>';
        this.loadFabrics().catch(() => {
          this.querySelector('[data-results]').innerHTML =
            '<p class="tdm-fabrics__empty">Les tissus n’ont pas pu être chargés. Fermez et réessayez.</p>';
        });
      }
      if (window.matchMedia('(min-width: 750px)').matches) this.querySelector('[data-search]')?.focus();
    }

    renderBrowser() {
      this.querySelector('[data-dialog-eyebrow]').textContent = `${this.modelName} · ${this.sizeLabel(this.size)}`;
      this.renderFilters();
      this.renderResults();
    }

    /** Values present in the data for each filter group. */
    facets() {
      const fabrics = this.fabrics || [];
      const count = (values) => {
        const map = new Map();
        values.forEach((value) => map.set(value, (map.get(value) || 0) + 1));
        return map;
      };
      const families = count(fabrics.flatMap((fabric) => fabric.families));
      const materials = count(fabrics.flatMap((fabric) => fabric.materials));
      const colours = count(fabrics.flatMap((fabric) => fabric.colours.map((colour) => colour.family)).filter(Boolean));
      const categories = count(fabrics.map((fabric) => fabric.category));
      const uses = count(fabrics.map((fabric) => fabric.use).filter(Boolean));
      return { families, materials, colours, categories, uses };
    }

    renderFilters() {
      const target = this.querySelector('[data-filters]');
      if (!this.fabrics) {
        target.innerHTML = '';
        return;
      }
      const { families, materials, colours, categories, uses } = this.facets();
      const chip = (group, value, label, extra = '') => {
        const pressed = this.filters[group].has(value);
        return `<button type="button" class="tdm-fabrics__chip" data-filter="${group}" data-value="${escapeHtml(value)}" aria-pressed="${pressed}">${extra}${escapeHtml(label)}</button>`;
      };

      const familyChips = [...families.keys()].sort((a, b) => a.localeCompare(b, 'fr')).map((value) => chip('fam', value, value));
      const materialChips = [...materials.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'fr'))
        .map(([value]) => chip('mat', value, value));
      const colourChips = COLOURS.filter((entry) => colours.has(entry.id)).map((entry) =>
        chip('col', entry.id, entry.label, `<span class="tdm-fabrics__dot" style="--dot:${entry.dot}"></span>`)
      );
      const size = this.size;
      const priceChips = this.categories
        .filter((category) => categories.has(category))
        .map((category) => {
          if (this.exact) return chip('cat', category, category);
          const price = this.priceFor(size, category);
          return chip('cat', category, price == null ? category : this.money.format(price / 100));
        });

      const useChips =
        uses.size > 1 ? SEAT_USE_ORDER.filter((use) => uses.has(use)).map((use) => chip('use', use, SEAT_USES[use].label)) : [];

      // One row of filter tabs; each opens its chips below (keeps the results in view on phones).
      const groups = [
        { id: 'col', label: 'Couleur', chips: colourChips },
        { id: 'use', label: 'Usage', chips: useChips },
        { id: 'fam', label: 'Aspect', chips: familyChips },
        { id: 'mat', label: 'Matière', chips: materialChips },
        { id: 'cat', label: 'Prix', chips: priceChips },
      ].filter((entry) => entry.chips.length);
      if (this.openGroup === undefined) {
        this.openGroup = window.matchMedia('(min-width: 750px)').matches ? groups[0]?.id ?? null : null;
      }
      const uid = this.data.productId;
      target.innerHTML = `
        <div class="tdm-fabrics__tabs">
          ${groups
            .map((entry) => {
              const active = this.filters[entry.id].size;
              return `<button type="button" class="tdm-fabrics__tab" data-group="${entry.id}"
                aria-expanded="${this.openGroup === entry.id}" aria-controls="tdm-fabrics-${uid}-${entry.id}">
                ${entry.label}${active ? `<span class="tdm-fabrics__tab-count">${active}</span>` : ''}${ICONS.chevronDown}
              </button>`;
            })
            .join('')}
        </div>
        ${groups
          .map(
            (entry) => `<div class="tdm-fabrics__chips" id="tdm-fabrics-${uid}-${entry.id}" role="group"
              aria-label="${entry.label}" ${this.openGroup === entry.id ? '' : 'hidden'}>${entry.chips.join('')}</div>`
          )
          .join('')}`;
    }

    toggleGroup(group) {
      this.openGroup = this.openGroup === group ? null : group;
      this.renderFilters();
      this.querySelector(`[data-group="${group}"]`)?.focus();
    }

    toggleFilter(group, value) {
      const set = this.filters[group];
      if (!set) return;
      if (set.has(value)) set.delete(value);
      else set.add(value);
      this.renderFilters();
      const chips = Array.from(this.querySelectorAll(`[data-filter="${group}"]`));
      chips.find((chip) => chip.dataset.value === value)?.focus();
      this.renderResults();
    }

    resetFilters() {
      Object.values(this.filters).forEach((set) => set.clear());
      this.query = '';
      const search = this.querySelector('[data-search]');
      if (search) search.value = '';
      this.renderFilters();
      this.renderResults();
    }

    matches(fabric, colour) {
      const { use, fam, mat, col, cat } = this.filters;
      if (use.size && !use.has(fabric.use)) return false;
      if (fam.size && !fabric.families.some((value) => fam.has(value))) return false;
      if (mat.size && !fabric.materials.some((value) => mat.has(value))) return false;
      if (cat.size && !cat.has(fabric.category)) return false;
      if (col.size && !col.has(colour.family)) return false;
      if (this.query) {
        const haystack = normalize(
          [fabric.title, colour.label, ...fabric.families, ...fabric.materials, fabric.composition].join(' ')
        );
        if (!this.query.split(/\s+/).every((word) => haystack.includes(word))) return false;
      }
      return true;
    }

    renderResults() {
      const results = this.querySelector('[data-results]');
      const count = this.querySelector('[data-count]');
      const reset = this.querySelector('[data-reset]');
      if (!this.fabrics) return;

      const size = this.size;
      const groups = this.fabrics
        .map((fabric) => ({ fabric, colours: fabric.colours.filter((colour) => this.matches(fabric, colour)) }))
        .filter((group) => group.colours.length);

      if (this.sort !== 'nom') {
        const direction = this.sort === 'prix' ? 1 : -1;
        groups.sort(
          (a, b) =>
            direction * ((this.priceFor(size, a.fabric) || 0) - (this.priceFor(size, b.fabric) || 0)) ||
            a.fabric.title.localeCompare(b.fabric.title, 'fr')
        );
      }

      const colourTotal = groups.reduce((sum, group) => sum + group.colours.length, 0);
      const filtered = this.query || Object.values(this.filters).some((set) => set.size);
      count.textContent = `${colourTotal} coloris · ${groups.length} tissu${groups.length > 1 ? 's' : ''}`;
      reset.hidden = !filtered;

      if (!groups.length) {
        results.innerHTML = `
          <div class="tdm-fabrics__empty">
            <p>Aucun tissu ne correspond à ces critères.</p>
            <button type="button" class="tdm-config__button" data-reset>Effacer les filtres</button>
          </div>`;
        this.renderFooter();
        return;
      }

      const pendingId = this.pending?.colour.id;
      const tile = (fabric, colour, withFabric) => {
        const reference = `${this.fabrics.indexOf(fabric)}:${fabric.colours.indexOf(colour)}`;
        const price = withFabric ? this.priceFor(size, fabric, this.variante, colour) : null;
        return `
          <button type="button" class="tdm-fabrics__swatch" data-pick="${reference}" aria-pressed="${colour.id === pendingId}"
            aria-label="${escapeHtml(`${fabric.title}, ${colour.label}`)}">
            ${image(colour.image, 'tdm-fabrics__swatch-image', 240)}
            <span class="tdm-fabrics__swatch-check" aria-hidden="true">${ICONS.check}</span>
            <span class="tdm-fabrics__swatch-name">${escapeHtml(colour.label)}</span>
            ${
              withFabric
                ? `<span class="tdm-fabrics__swatch-fabric">${escapeHtml(fabric.title)}${
                    price != null ? ` · ${this.money.format(price / 100)}` : ''
                  }</span>`
                : ''
            }
          </button>`;
      };

      // Looking for a colour (or searching): one wall of swatches. Otherwise: grouped by fabric.
      if (this.filters.col.size || this.query) {
        results.innerHTML = `<div class="tdm-fabrics__swatches tdm-fabrics__swatches--flat">${groups
          .flatMap(({ fabric, colours }) => colours.map((colour) => tile(fabric, colour, true)))
          .join('')}</div>`;
        this.renderFooter();
        return;
      }

      // Sorted by name: one heading per seat use (recommended fabrics first).
      const headings = this.sort === 'nom' && new Set(groups.map(({ fabric }) => fabric.use || 'tous')).size > 1;
      results.innerHTML = groups
        .map(({ fabric, colours }, index) => {
          // Fabrics with a seat use in their specs only (no recommendation) sit with "tous sièges".
          const use = fabric.use || 'tous';
          const heading =
            headings && use !== (groups[index - 1]?.fabric.use || (index ? 'tous' : null))
              ? `<h3 class="tdm-fabrics__use">${escapeHtml(SEAT_USES[use].heading)}</h3>`
              : '';
          const warning = this.useWarning(fabric);
          const price = this.priceFor(size, fabric);
          const meta = [...fabric.families, fabric.materials.join(', '), fabric.abrasion && `${formatNumber(fabric.abrasion)} cycles`]
            .filter(Boolean)
            .join(' · ');
          return `${heading}
            <section class="tdm-fabrics__fabric">
              <header class="tdm-fabrics__fabric-head">
                <div>
                  <h3 class="tdm-fabrics__fabric-name">${escapeHtml(fabric.title)}</h3>
                  ${meta ? `<p class="tdm-fabrics__fabric-meta">${escapeHtml(meta)}</p>` : ''}
                  ${warning ? `<p class="tdm-fabrics__warning">${ICONS.info}<span>${escapeHtml(warning)}</span></p>` : ''}
                </div>
                ${
                  price != null
                    ? `<span class="tdm-fabrics__fabric-price">${this.exact && new Set(fabric.colours.map((c) => c.unitPrice)).size > 1 ? 'dès ' : ''}${this.money.format(price / 100)}</span>`
                    : ''
                }
              </header>
              <div class="tdm-fabrics__swatches">
                ${colours.map((colour) => tile(fabric, colour, false)).join('')}
              </div>
            </section>`;
        })
        .join('');
      this.renderFooter();
    }

    renderFooter() {
      const footer = this.querySelector('[data-footer]');
      const pending = this.pending;
      if (!pending) {
        footer.innerHTML = `
          <p class="tdm-fabrics__hint">Touchez un coloris pour le sélectionner.</p>
          <button type="button" class="tdm-fabrics__commit" disabled>Valider</button>`;
        return;
      }
      const price = this.priceFor(this.size, pending.fabric, this.variante, pending.colour);
      footer.innerHTML = `
        <div class="tdm-fabrics__picked">
          ${image(pending.colour.image, 'tdm-fabrics__picked-swatch', 80)}
          <p><strong>${escapeHtml(pending.fabric.title)}</strong> ${escapeHtml(pending.colour.label)}
            ${price != null ? `<span>${escapeHtml(this.sizeLabel(this.size))} · ${this.money.format(price / 100)}</span>` : ''}
            ${this.useWarning(pending.fabric) ? `<span class="tdm-fabrics__picked-warning">${escapeHtml(this.useWarning(pending.fabric))}</span>` : ''}</p>
        </div>
        <button type="button" class="tdm-fabrics__commit" data-commit>Valider ce tissu</button>`;
    }

    pick(reference) {
      const [fabricIndex, colourIndex] = reference.split(':').map(Number);
      const fabric = this.fabrics?.[fabricIndex];
      const colour = fabric?.colours[colourIndex];
      if (!colour) return;
      // A second tap on the selected colour confirms it.
      if (this.pending?.colour.id === colour.id) {
        this.commit();
        return;
      }
      this.pending = { fabric, colour };
      this.querySelectorAll('[data-pick]').forEach((tile) => {
        tile.setAttribute('aria-pressed', String(tile.dataset.pick === reference));
      });
      this.renderFooter();
    }

    commit() {
      if (!this.pending) return;
      this.select(this.pending.fabric, this.pending.colour);
      this.dialog.close();
      // Keyboard users land back on the fabric step; a mouse or touch choice keeps the page still.
      if (this.keyboardClick) this.querySelector('[data-fabric-slot] [data-open]')?.focus({ preventScroll: true });
    }

    select(fabric, colour) {
      this.fabric = fabric;
      this.colour = colour;
      this.hideError();
      if (!this.exact) this.setPicker([fabric.category]);
      if (this.tiered) this.setPicker([this.size, this.variante, this.fabricPickerValue]);
      this.renderSizes();
      this.renderFabricSlot();
      this.renderRecap();
      this.syncProperties();
      this.updatePagePrice();
      writeStorage(FABRIC_STORAGE_KEY, { h: fabric.handle, i: colour.id });
    }

    restoreSelection() {
      if (this.fabric) return;
      const saved = readStorage(FABRIC_STORAGE_KEY);
      const fabric = saved && this.fabrics.find((entry) => entry.handle === saved.h);
      const colour = fabric?.colours.find((entry) => entry.id === saved.i);
      if (colour) this.select(fabric, colour);
    }

    /* ---------- Review before the cart ---------- */

    get quantity() {
      const input = this.form?.querySelector('input[name="quantity"]');
      return Math.max(1, Number(input?.value) || 1);
    }

    openReview() {
      const spec = this.specFor(this.size);
      const quantity = this.quantity;
      const price = this.total;
      const rows = [
        ...this.summaryRows(),
        ...(this.activeExtras.length ? [['Ajouté', this.activeExtras.map((extra) => extra.nom).join(' · ')]] : []),
        ['Composition', this.fabric.composition],
        ['Usage du tissu', this.useWarning(this.fabric)],
        ['Fabrication', this.leadTime ? `Sur commande · ${this.leadTime}` : 'Sur commande'],
        ...(quantity > 1 ? [['Quantité', String(quantity)]] : []),
      ].filter(([, value]) => value);
      const uid = this.data.productId;
      const noun = this.noun;
      const notes = this.notes.length ? this.notes.map((note) => `<br>${escapeHtml(note)}`).join('') : '';
      this.querySelector('[data-review-body]').innerHTML = `
        <header class="tdm-sheet__header">
          <div>
            <p class="tdm-sheet__eyebrow">Récapitulatif</p>
            <h2 class="tdm-sheet__title" id="tdm-review-title-${uid}">Vérifiez votre ${escapeHtml(noun)}</h2>
          </div>
          <button type="button" class="tdm-sheet__close" data-close aria-label="Fermer">${ICONS.close}</button>
        </header>
        <div class="tdm-sheet__body">
          <div class="tdm-review__visuals">
            <figure class="tdm-review__photo">
              ${this.data.image ? `<img src="${escapeHtml(this.data.image)}" alt="" loading="lazy">` : ''}
              <figcaption>Photo non contractuelle, modèle présenté dans un autre tissu</figcaption>
            </figure>
            <div class="tdm-review__plan">
              ${planSvg(spec, this.colour.image, this.side)}
              <div class="tdm-review__swatch">
                ${image(this.colour.image, 'tdm-review__swatch-image', 120)}
                <span><strong>${escapeHtml(this.fabric.title)}</strong> ${escapeHtml(this.colour.label)}</span>
              </div>
            </div>
          </div>
          <dl class="tdm-review__list">
            ${rows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}
          </dl>
          <p class="tdm-review__note">Chaque pièce est fabriquée pour vous : une fois la fabrication lancée, la commande
            ne peut plus être modifiée. Notre service Sièges et Canapés vous contacte pour confirmer la livraison.${notes}</p>
        </div>
        <footer class="tdm-sheet__footer">
          <div class="tdm-review__total">
            <span>Total TTC${quantity > 1 ? ` · ${quantity} pièces` : ''}</span>
            <strong>${price != null ? this.money.format((price * quantity) / 100) : ''}</strong>
          </div>
          ${this.breakdownMarkup('tdm-review__breakdown', quantity)}
          <p class="tdm-review__error" data-review-error role="alert" hidden></p>
          <div class="tdm-sheet__actions">
            <button type="button" class="tdm-config__button" data-review-edit>Modifier</button>
            <button type="button" class="tdm-sheet__primary" data-review-confirm>Ajouter au panier</button>
          </div>
          <button type="button" class="tdm-sheet__link" data-quote>Une question avant de commander ? Demander un devis</button>
        </footer>`;
      this.openDialog(this.review);
      this.review.querySelector('[data-review-confirm]')?.focus();
    }

    confirmReview() {
      if (this.exact && !this.tiered) {
        this.addToCart();
        return;
      }
      if (this.activeExtras.length) {
        this.addWithExtras();
        return;
      }
      this.review.close();
      this.confirmed = true;
      const button = this.pendingButton;
      this.pendingButton = null;
      if (button?.isConnected) button.click();
      else this.form?.requestSubmit();
    }

    /* ---------- Quote / question (Shopify contact form) ---------- */

    openQuote(option = '') {
      if (this.review?.open) this.review.close();
      const rows = this.summaryRows();
      const price = this.price;
      this.querySelector('[data-quote-config]').innerHTML = `
        <p class="tdm-quote__config-title">Votre configuration, jointe à la demande</p>
        <dl class="tdm-review__list tdm-review__list--compact">
          ${rows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}
          ${price != null ? `<div><dt>Prix affiché</dt><dd>${this.money.format(price / 100)}</dd></div>` : ''}
          ${option ? `<div><dt>Option</dt><dd>${escapeHtml(option)}</dd></div>` : ''}
        </dl>`;
      this.quoteOption = option;
      this.fillQuote();
      this.openDialog(this.quoteDialog);
    }

    /** Hidden fields of the contact form: the seat as configured, sent with the message. */
    fillQuote() {
      const form = this.querySelector('[data-quote-form] form');
      if (!form) return;
      const rows = this.summaryRows();
      const values = Object.fromEntries(rows);
      const set = (name, value) => {
        const input = form.querySelector(`input[name="contact[${name}]"]`);
        if (input) input.value = value || '';
      };
      set('Modèle', this.data.title);
      set('Dimension', values.Dimension);
      set('Option', this.variantOption ? `${this.variantOption} : ${values[this.variantOption] || ''}` : '');
      set('Finitions', this.activeChoices.map((choice) => `${choice.nom} : ${this.choiceValue(choice)}`).join(' · '));
      set('Tissu', this.fabric ? this.fabric.title : '');
      set('Coloris', this.colour ? this.colour.label : '');
      set('Réf. tissu', this.colour ? this.colour.sku : '');
      set('Prix affiché', this.price != null ? this.money.format(this.price / 100) : '');
      set('Option demandée', this.quoteOption || '');
      set('Page', window.location.href.split('#')[0]);
    }

    showBanner() {
      const banner = this.querySelector('[data-banner]');
      banner.innerHTML = `${ICONS.check}<p><strong>Merci, votre demande est envoyée.</strong>
        Notre service Sièges et Canapés vous répond rapidement par e-mail ou par téléphone.</p>`;
      banner.hidden = false;
    }

    /* ---------- Cart ---------- */

    get complete() {
      return Boolean(this.fabric && this.colour);
    }

    /**
     * Line item properties on the cart form (removed while the choice is incomplete):
     * the fabric, the second option's label, the free choices, the lead time and the metres.
     */
    syncProperties() {
      const form = this.form;
      if (!form) return;
      const spec = this.specFor(this.size);
      const variante = this.variante;
      const values = this.complete
        ? {
            fabric: this.fabric.title,
            colour: this.colour.label,
            reference: this.colour.sku || '',
            leadTime: this.leadTime ? `Sur commande · ${this.leadTime}` : 'Sur commande',
            yardage: (() => {
              const metres = this.metresFor(this.size, this.fabric);
              const patterned = this.isPatterned(this.fabric) && Number(spec.metrage_motif);
              return metres ? `${formatNumber(metres, 2)} m (${patterned ? 'motif' : 'uni'})` : '';
            })(),
          }
        : {};
      const entries = Object.entries(PROPERTIES).map(([key, name]) => [name, values[key]]);
      if (this.complete && variante && this.variantLabels[variante]?.libelle) {
        entries.push([`properties[${this.variantOption}]`, this.variantLabel(variante)]);
      }
      if (this.complete) {
        this.activeChoices.forEach((choice) => entries.push([`properties[${choice.nom}]`, this.choiceValue(choice)]));
      }

      // Remove the properties written earlier that no longer apply (choices of another size…).
      const wanted = new Set(entries.filter(([, value]) => value).map(([name]) => name));
      form.querySelectorAll('input[data-tdm-config-property]').forEach((input) => {
        if (!wanted.has(input.name)) input.remove();
      });
      entries.forEach(([name, value]) => {
        if (!value) return;
        let input = form.querySelector(`input[data-tdm-config-property][name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.dataset.tdmConfigProperty = '';
          form.appendChild(input);
        }
        input.value = value;
      });
    }

    /**
     * Add-to-cart (button, sticky bar or form submit): blocked until a fabric colour is
     * chosen, then shown as a review the customer confirms before it goes to the cart.
     */
    guard(event) {
      const form = this.form;
      if (!form) return;
      let button = null;
      if (event.type === 'submit') {
        if (event.target !== form) return;
      } else {
        button = event.target.closest?.('button');
        if (!button || button.type !== 'submit' || (button.form !== form && !form.contains(button))) return;
      }

      if ((!this.exact || this.tiered) && this.complete && this.confirmed) {
        if (event.type === 'submit') this.confirmed = false;
        this.syncProperties();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      // The TDM sticky bar shows "loading" then "added" after clicking the main button;
      // cart:error resets it, since nothing was added.
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'tdm-canape-configurator' } }));

      if (this.complete) {
        this.pendingButton = button || form.querySelector('button[type="submit"]');
        this.openReview();
        return;
      }

      const error = this.querySelector('[data-error]');
      error.textContent = `Choisissez le tissu et le coloris de votre ${this.noun}.`;
      error.hidden = false;
      const step = this.querySelector('[data-fabric-slot]');
      step.scrollIntoView({ behavior: 'smooth', block: 'center' });
      step.querySelector('[data-open]')?.focus({ preventScroll: true });
    }

    hideError() {
      const error = this.querySelector('[data-error]');
      if (error) error.hidden = true;
    }

    /* ---------- Sample (same SKU rule as the fabric pages: <fabric SKU>E) ---------- */

    async findSampleVariant(sampleSku) {
      for (let page = 1; page <= 4; page += 1) {
        const response = await fetch(`${SAMPLE_COLLECTION}?limit=250&page=${page}`);
        if (!response.ok) return null;
        const { products = [] } = await response.json();
        for (const product of products) {
          const match = (product.variants || []).find((variant) => variant.sku === sampleSku);
          if (match) return match.id;
        }
        if (products.length < 250) return null;
      }
      return null;
    }

    async addSample(button) {
      if (!this.colour?.sku || button.disabled) return;
      const label = button.querySelector('span');
      const original = label.textContent;
      const reset = (text) => {
        label.textContent = text;
        setTimeout(() => {
          label.textContent = original;
          button.disabled = false;
        }, 2500);
      };

      button.disabled = true;
      label.textContent = 'Ajout en cours…';
      try {
        const sampleSku = `${String(this.colour.sku).replace(/-O$/i, '')}E`;
        const id = await this.findSampleVariant(sampleSku);
        if (!id) {
          reset('Échantillon non disponible');
          return;
        }
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id, quantity: 1 }),
        });
        if (!response.ok) throw new Error('cart/add failed');
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: {}, sourceId: 'tdm-sample-order', data: { source: 'tdm-sample-order', sections: {} } },
          })
        );
        document.querySelector('cart-drawer-component')?.open?.();
        reset('Échantillon ajouté au panier');
      } catch (error) {
        reset('Erreur, réessayez');
      }
    }
  }

  customElements.define('tdm-canape-configurator', TdmCanapeConfigurator);
})();
